// src/components/DocumentIntelligence.jsx
//
// Reusable, presentation-agnostic document-intelligence workspace for AskLawly.
// UI readability/layout refinement + File Access Gateway handoff: 2026.09.03-v5-unavailable-reconciliation
// Ask-this-document is a composer context action, not a one-click analysis action.
// View File is a zero-billing document utility that asks the parent to open the
// ORIGINAL uploaded file through the shared File Access Gateway. This component
// never constructs gateway URLs, auth headers, browser tickets, or storage paths.
//
// Primary presentation modes:
// - presentation="dock"   -> persistent desktop/tablet floating panel + mobile bottom sheet
// - presentation="inline" -> in-thread document control card
//
// Single-file build: component logic + responsive styling live together here.
//
// Reuse contract:
// - Dock mode is a self-contained authenticated Document Intelligence catalog.
//   It reads the shared Main API directly, loads 10 owner-visible documents at a
//   time, switches documentId-specific projections, polls active-document status,
//   and performs user soft-delete through the shared DELETE endpoint.
// - Inline mode remains parent-driven and renders only the document supplied by
//   that parent. It never expands into the owner's full document inventory.
//
// This component never calls AI or storage directly. It only consumes the safe,
// owner-scoped Document Intelligence HTTP projection exposed by Main.

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  FiActivity,
  FiAlertTriangle,
  FiBookOpen,
  FiCalendar,
  FiCheck,
  FiCheckCircle,
  FiChevronDown,
  FiChevronsLeft,
  FiChevronsRight,
  FiClock,
  FiDollarSign,
  FiFileText,
  FiEye,
  FiLayers,
  FiList,
  FiMap,
  FiMessageSquare,
  FiMinimize2,
  FiMoreHorizontal,
  FiRefreshCw,
  FiSearch,
  FiShield,
  FiTrash2,
  FiUsers,
  FiX,
  FiZap,
} from "react-icons/fi";

const BRAND = "#1E2A78";
const MOBILE_BREAKPOINT = 640;
const TABLET_BREAKPOINT = 1024;

const DEFAULT_CATALOG_PAGE_SIZE = 10;
const MAX_CATALOG_PAGE_SIZE = 50;
const STATUS_POLL_INTERVAL_MS = 5000;
const DOCK_HOVER_OPEN_DELAY_MS = 750;
const DOCK_BOTTOM_SAFETY_PX = 24;
const DOCK_MIN_VISIBLE_HEIGHT_PX = 280;
const PAGE_SECTION_INITIAL_VISIBLE = 4;
const PAGE_SECTION_REVEAL_STEP = 6;

// -----------------------------------------------------------------------------
// SINGLE-FILE STYLE OWNERSHIP
// -----------------------------------------------------------------------------
// Document Intelligence deliberately owns its stylesheet so consumers only import one file.
// A single namespaced <style> element is installed in <head> and reference-counted across
// multiple mounted Document Intelligence instances (for example an inline card + floating document intelligence panel).
// Existing host-owned styles with the same id are respected and never removed.

const DOCUMENT_INTELLIGENCE_STYLE_ID = "asklawly-document-intelligence-styles";
const DOCUMENT_INTELLIGENCE_CSS = String.raw`.document-intelligence {
  --di-brand: #1E2A78;
  --di-text: #111827;
  --di-muted: #667085;
  --di-border: rgba(15, 23, 42, 0.09);
  --di-soft-border: rgba(15, 23, 42, 0.065);
  --di-soft-bg: #f7f8fb;
  --di-card-bg: rgba(255, 255, 255, 0.98);
  position: relative;
  box-sizing: border-box;
  width: 100%;
  color: var(--di-text);
  background: var(--di-card-bg);
  font-family: inherit;
  -webkit-font-smoothing: antialiased;
}

.document-intelligence *,
.document-intelligence *::before,
.document-intelligence *::after {
  box-sizing: border-box;
}

.di-floating-shell {
  position: fixed;
  z-index: 1150;
  pointer-events: none;
  max-width: 100vw;
}

.di-shell-desktop {
  right: 20px;
  top: max(76px, calc(env(safe-area-inset-top, 0px) + 64px));
  bottom: max(24px, calc(env(safe-area-inset-bottom, 0px) + 18px));
  width: min(448px, calc(100vw - 40px));
  display: flex;
  align-items: flex-start;
}

.di-shell-tablet {
  right: max(12px, env(safe-area-inset-right, 0px));
  top: max(68px, calc(env(safe-area-inset-top, 0px) + 56px));
  bottom: max(16px, calc(env(safe-area-inset-bottom, 0px) + 12px));
  width: min(420px, calc(100vw - 30px));
  display: flex;
  align-items: flex-start;
}

.di-shell-mobile {
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  display: flex;
  align-items: flex-end;
}

.di-dock {
  pointer-events: auto;
  border: 1px solid var(--di-border);
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.16), 0 2px 10px rgba(15, 23, 42, 0.08);
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.di-dock.di-desktop,
.di-dock.di-tablet {
  max-height: 100%;
  border-radius: 20px;
  padding: 14px;
}

.di-dock.di-mobile {
  max-height: min(82dvh, 760px);
  border-radius: 24px 24px 0 0;
  border-bottom: none;
  padding: 8px 14px max(16px, calc(env(safe-area-inset-bottom, 0px) + 12px));
  box-shadow: 0 -18px 56px rgba(15, 23, 42, 0.22);
  animation: diSheetIn 180ms ease-out;
}

.di-inline {
  border: 1px solid rgba(30, 42, 120, 0.11);
  border-radius: 18px;
  padding: 14px;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.07);
  overflow: visible;
}

.di-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 42px;
}

.di-header-main {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.di-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: inline-grid;
  place-items: center;
  flex: 0 0 auto;
  color: var(--di-brand);
  background: color-mix(in srgb, var(--di-brand) 9%, white);
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--di-brand) 10%, transparent);
  font-size: 18px;
}

.di-header-copy {
  min-width: 0;
}

.di-eyebrow {
  font-size: 9.5px;
  line-height: 1;
  font-weight: 800;
  letter-spacing: 0.11em;
  color: var(--di-brand);
  margin-bottom: 4px;
}

.di-title {
  font-size: 16px;
  line-height: 1.2;
  font-weight: 800;
  letter-spacing: -0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.di-header-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.di-drag-handle {
  width: 28px;
  height: 34px;
  border: none;
  background: transparent;
  color: #98a2b3;
  display: inline-grid;
  place-items: center;
  cursor: grab;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  font-size: 16px;
  border-radius: 9px;
  transition: color 120ms ease, background 120ms ease;
}

.di-drag-handle:hover,
.di-drag-handle:focus-visible {
  color: var(--di-brand);
  background: #f4f6f9;
}

.di-drag-handle.is-dragging {
  cursor: grabbing;
  color: var(--di-brand);
  background: #eef1f8;
}

.di-drag-handle:active {
  cursor: grabbing;
}

.di-drag-handle svg {
  transform: rotate(90deg);
}

.di-icon-button {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid transparent;
  background: transparent;
  color: #5f6b7a;
  display: inline-grid;
  place-items: center;
  cursor: pointer;
  font-size: 17px;
  transition: background 120ms ease, border-color 120ms ease, color 120ms ease, transform 120ms ease;
}

.di-icon-button:hover {
  background: #f4f6f9;
  border-color: var(--di-soft-border);
  color: var(--di-brand);
}

.di-icon-button:active {
  transform: scale(0.96);
}

.di-sheet-grabber {
  width: 42px;
  height: 4px;
  border-radius: 999px;
  background: #d4d8e0;
  margin: 2px auto 8px;
}

.di-scope-block {
  position: relative;
  z-index: 4;
  margin-top: 12px;
}

.di-document-selector {
  width: 100%;
  min-height: 56px;
  padding: 8px 10px;
  border-radius: 14px;
  border: 1px solid var(--di-border);
  background: #fbfcfe;
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) 24px;
  align-items: center;
  gap: 9px;
  text-align: left;
  cursor: pointer;
  transition: border-color 140ms ease, box-shadow 140ms ease, background 140ms ease;
}

.di-document-selector:hover,
.di-document-selector.is-open {
  border-color: color-mix(in srgb, var(--di-brand) 24%, transparent);
  background: #ffffff;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.055);
}

.di-selector-icon,
.di-document-option-icon {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: inline-grid;
  place-items: center;
  background: #eef1f8;
  color: var(--di-brand);
  flex: 0 0 auto;
}

.di-selector-copy,
.di-document-option-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.di-selector-copy strong,
.di-document-option-copy strong {
  font-size: 13.3px;
  line-height: 1.25;
  font-weight: 750;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.di-selector-copy small,
.di-document-option-copy small {
  color: var(--di-muted);
  font-size: 11px;
  line-height: 1.25;
}

.di-selector-chevron {
  color: #7b8493;
  transition: transform 140ms ease;
}

.di-document-selector.is-open .di-selector-chevron {
  transform: rotate(180deg);
}

.di-document-list {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 7px);
  z-index: 20;
  padding: 6px;
  max-height: min(340px, 48dvh);
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  border: 1px solid var(--di-border);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.99);
  box-shadow: 0 18px 44px rgba(15, 23, 42, 0.15);
}

.di-document-option {
  width: 100%;
  min-height: 48px;
  padding: 7px 8px;
  border: none;
  border-radius: 10px;
  background: transparent;
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr) 20px;
  align-items: center;
  gap: 8px;
  text-align: left;
  cursor: pointer;
  color: inherit;
}

.di-document-option:hover {
  background: #f6f7fa;
}

.di-document-option.is-active {
  background: color-mix(in srgb, var(--di-brand) 7%, white);
}

.di-document-list-footer {
  position: sticky;
  bottom: -6px;
  margin: 5px -1px -1px;
  padding: 7px 5px 5px;
  background: linear-gradient(to bottom, rgba(255,255,255,0.84), #ffffff 38%);
}

.di-load-more {
  width: 100%;
  min-height: 36px;
  border: 1px solid var(--di-border);
  border-radius: 10px;
  background: #f8fafc;
  color: #344054;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  font: inherit;
  font-size: 10.8px;
  font-weight: 750;
  cursor: pointer;
}

.di-load-more:hover:not(:disabled) {
  background: #ffffff;
  border-color: color-mix(in srgb, var(--di-brand) 20%, transparent);
  color: var(--di-brand);
}

.di-load-more:disabled {
  opacity: 0.58;
  cursor: default;
}

.di-catalog-error {
  padding: 7px 5px 2px;
  color: #b42318;
  font-size: 9.5px;
  line-height: 1.35;
}

.di-option-check {
  color: var(--di-brand);
}

.di-active-document,
.di-all-documents-summary {
  margin-top: 12px;
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  min-height: 58px;
}

.di-all-documents-summary {
  grid-template-columns: 42px minmax(0, 1fr);
}

.di-file-mark {
  width: 42px;
  height: 48px;
  border-radius: 10px;
  display: inline-grid;
  place-items: center;
  color: var(--di-brand);
  background: linear-gradient(145deg, #f8f9fd, #eef1f8);
  border: 1px solid rgba(30, 42, 120, 0.10);
  font-size: 19px;
  flex: 0 0 auto;
}

.di-active-document-copy {
  min-width: 0;
  padding: 0;
  border: none;
  background: transparent;
  color: inherit;
  text-align: left;
}

.di-document-utilities {
  min-width: 0;
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;
}

.di-view-file-button {
  min-height: 32px;
  padding: 6px 9px;
  border: 1px solid rgba(30, 42, 120, 0.13);
  border-radius: 9px;
  background: #f8f9fd;
  color: var(--di-brand);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font: inherit;
  font-size: 10.4px;
  line-height: 1;
  font-weight: 800;
  white-space: nowrap;
  cursor: pointer;
  transition: background 120ms ease, border-color 120ms ease, transform 120ms ease, box-shadow 120ms ease;
}

.di-view-file-button:hover {
  background: #ffffff;
  border-color: color-mix(in srgb, var(--di-brand) 26%, transparent);
  box-shadow: 0 5px 14px rgba(30, 42, 120, 0.07);
}

.di-view-file-button:active {
  transform: scale(0.98);
}

.di-view-file-button svg {
  flex: 0 0 auto;
  font-size: 13px;
}

.di-active-document-copy strong,
.di-all-documents-summary strong {
  display: block;
  font-size: 14px;
  line-height: 1.3;
  font-weight: 800;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.di-active-document-copy > span,
.di-all-documents-summary > div > span {
  margin-top: 4px;
  min-height: 19px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 7px;
  color: var(--di-muted);
  font-size: 11.2px;
}


.di-chat-use {
  position: relative;
  margin-top: 10px;
  padding: 11px 11px 10px 13px;
  overflow: hidden;
  border: 1px solid rgba(30, 42, 120, 0.17);
  border-radius: 14px;
  background: linear-gradient(135deg, #f8faff 0%, #f4f6fb 58%, #fafbfe 100%);
  box-shadow: 0 6px 18px rgba(30, 42, 120, 0.055);
}

.di-chat-use::before {
  content: "";
  position: absolute;
  left: 0;
  top: 10px;
  bottom: 10px;
  width: 3px;
  border-radius: 0 999px 999px 0;
  background: color-mix(in srgb, var(--di-brand) 72%, white);
}

.di-chat-use.is-off {
  border-color: rgba(71, 84, 103, 0.14);
  background: linear-gradient(135deg, #fafbfc, #f5f6f8);
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.04);
}

.di-chat-use.is-off::before {
  background: #98a2b3;
}

.di-chat-use-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.di-chat-use-copy {
  min-width: 0;
  display: grid;
  grid-template-columns: 26px minmax(0, 1fr);
  align-items: center;
  gap: 8px;
}

.di-chat-use-copy-icon {
  width: 26px;
  height: 26px;
  border-radius: 8px;
  display: inline-grid;
  place-items: center;
  color: var(--di-brand);
  background: rgba(255, 255, 255, 0.78);
  box-shadow: inset 0 0 0 1px rgba(30, 42, 120, 0.10);
  font-size: 13px;
}

.di-chat-use.is-off .di-chat-use-copy-icon {
  color: #667085;
  box-shadow: inset 0 0 0 1px rgba(71, 84, 103, 0.10);
}

.di-chat-use-copy-text {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.di-chat-use-copy strong {
  color: #29324a;
  font-size: 11.4px;
  line-height: 1.2;
  font-weight: 800;
  letter-spacing: 0.025em;
}

.di-chat-use-copy span {
  color: #667085;
  font-size: 10.1px;
  line-height: 1.25;
}

.di-chat-use-segment {
  flex: 0 0 auto;
  display: inline-grid;
  grid-template-columns: repeat(3, auto);
  gap: 3px;
  padding: 3px;
  border: 1px solid rgba(30, 42, 120, 0.08);
  border-radius: 11px;
  background: #e9edf5;
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.035);
}

.di-chat-use-option {
  min-height: 30px;
  padding: 5px 9px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #667085;
  font: inherit;
  font-size: 10.3px;
  line-height: 1;
  font-weight: 800;
  white-space: nowrap;
  cursor: pointer;
  transition: background 120ms ease, color 120ms ease, box-shadow 120ms ease, transform 120ms ease;
}

.di-chat-use-option:hover:not(.is-active) {
  color: var(--di-brand);
  background: rgba(255, 255, 255, 0.62);
}

.di-chat-use-option:active {
  transform: scale(0.98);
}

.di-chat-use-option.is-active {
  color: var(--di-brand);
  background: #ffffff;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.12), inset 0 0 0 1px rgba(30, 42, 120, 0.10);
}

.di-chat-use-option.is-off.is-active {
  color: #344054;
  background: #ffffff;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.10), inset 0 0 0 1px rgba(71, 84, 103, 0.12);
}

.di-chat-use-help {
  margin: 8px 2px 0;
  color: #5f6b7a;
  font-size: 10.15px;
  line-height: 1.42;
}

.di-chat-use.is-off .di-chat-use-help {
  color: #667085;
}

.di-section-focus {
  margin-top: 10px;
  overflow: hidden;
  border: 1px solid rgba(30, 42, 120, 0.11);
  border-radius: 13px;
  background: #fbfcfe;
}

.di-section-focus.is-disabled {
  background: #fafafa;
}

.di-section-focus-toggle {
  width: 100%;
  min-height: 52px;
  padding: 8px 10px;
  border: none;
  background: transparent;
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) auto 18px;
  align-items: center;
  gap: 8px;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.di-section-focus-toggle:hover:not(:disabled) {
  background: #ffffff;
}

.di-section-focus-toggle:disabled {
  cursor: default;
}

.di-section-focus-icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: inline-grid;
  place-items: center;
  color: var(--di-brand);
  background: color-mix(in srgb, var(--di-brand) 7%, white);
  font-size: 14px;
}

.di-section-focus.is-disabled .di-section-focus-icon {
  color: #98a2b3;
  background: #f2f4f7;
}

.di-section-focus-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.di-section-focus-copy strong {
  color: #344054;
  font-size: 11.5px;
  line-height: 1.2;
  font-weight: 800;
}

.di-section-focus-copy span {
  color: var(--di-muted);
  font-size: 10.2px;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.di-section-focus-count {
  color: #667085;
  font-size: 9.8px;
  font-weight: 700;
  white-space: nowrap;
}

.di-section-focus-chevron {
  color: #98a2b3;
  transition: transform 140ms ease;
}

.di-section-focus-toggle.is-open .di-section-focus-chevron {
  transform: rotate(180deg);
}

.di-section-focus-body {
  padding: 0 10px 10px;
  border-top: 1px solid var(--di-soft-border);
}

.di-section-focus-note {
  margin: 9px 1px;
  color: #667085;
  font-size: 10.1px;
  line-height: 1.38;
}

.di-section-focus-disabled-note {
  margin: 8px 1px 0;
  padding: 7px 8px;
  border-radius: 9px;
  background: #f2f4f7;
  color: #667085;
  font-size: 10px;
  line-height: 1.4;
}

.di-page-scope-chip {
  margin: 8px 0 3px;
  min-height: 38px;
  padding: 6px 7px 6px 9px;
  border: 1px solid rgba(30, 42, 120, 0.13);
  border-radius: 10px;
  background: color-mix(in srgb, var(--di-brand) 5%, white);
  display: grid;
  grid-template-columns: minmax(0, 1fr) 26px;
  align-items: center;
  gap: 7px;
}

.di-page-scope-chip-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.di-page-scope-chip-copy strong {
  color: var(--di-brand);
  font-size: 10.5px;
  line-height: 1.2;
}

.di-page-scope-chip-copy span {
  color: #667085;
  font-size: 10px;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.di-page-scope-clear {
  width: 26px;
  height: 26px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #667085;
  display: inline-grid;
  place-items: center;
  cursor: pointer;
}

.di-page-scope-clear:hover:not(:disabled) {
  color: var(--di-brand);
  background: #ffffff;
}

.di-page-scope-clear:disabled {
  cursor: default;
  opacity: 0.5;
}

.di-page-range-form {
  margin-top: 8px;
  padding: 8px;
  border: 1px solid var(--di-soft-border);
  border-radius: 10px;
  background: #ffffff;
}

.di-page-range-form-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}

.di-page-range-form-title strong {
  color: #475467;
  font-size: 10.3px;
  font-weight: 800;
}

.di-page-range-form-title span {
  color: #98a2b3;
  font-size: 9.5px;
}

.di-page-range-fields {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
  align-items: end;
  gap: 6px;
}

.di-page-range-field {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.di-page-range-field label {
  color: #667085;
  font-size: 9.6px;
  font-weight: 700;
}

.di-page-range-field input {
  width: 100%;
  min-height: 34px;
  padding: 6px 8px;
  border: 1px solid rgba(15, 23, 42, 0.11);
  border-radius: 8px;
  background: #ffffff;
  color: #344054;
  font: inherit;
  font-size: 10.8px;
  outline: none;
}

.di-page-range-field input:focus {
  border-color: color-mix(in srgb, var(--di-brand) 32%, transparent);
  box-shadow: 0 0 0 3px rgba(30, 42, 120, 0.08);
}

.di-page-range-apply {
  min-height: 34px;
  padding: 6px 10px;
  border: 1px solid rgba(30, 42, 120, 0.15);
  border-radius: 8px;
  background: #f7f8fd;
  color: var(--di-brand);
  font: inherit;
  font-size: 10.1px;
  font-weight: 800;
  cursor: pointer;
}

.di-page-range-apply:hover:not(:disabled) {
  background: #ffffff;
}

.di-page-range-apply:disabled,
.di-page-range-field input:disabled {
  opacity: 0.55;
  cursor: default;
}

.di-page-range-error {
  margin: 5px 1px 0;
  color: #b42318;
  font-size: 9.6px;
  line-height: 1.3;
}

.di-status {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 7px;
  border-radius: 999px;
  font-size: 10.1px;
  font-weight: 750;
}

.di-status-ready {
  color: #067647;
  background: #ecfdf3;
}

.di-status-working {
  color: #1d4ed8;
  background: #eff6ff;
}

.di-status-error {
  color: #b42318;
  background: #fef3f2;
}

.di-overflow-wrap {
  position: relative;
}

.di-overflow-menu {
  position: absolute;
  z-index: 25;
  right: 0;
  top: calc(100% + 5px);
  width: 196px;
  padding: 5px;
  background: white;
  border: 1px solid var(--di-border);
  border-radius: 12px;
  box-shadow: 0 16px 38px rgba(15, 23, 42, 0.15);
}

.di-overflow-menu button {
  width: 100%;
  min-height: 38px;
  border: none;
  border-radius: 8px;
  background: transparent;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 9px;
  font: inherit;
  font-size: 11.5px;
  color: #344054;
  cursor: pointer;
  text-align: left;
}

.di-overflow-menu button:hover {
  background: #f6f7f9;
}

.di-overflow-menu button.is-danger {
  color: #b42318;
}

.di-divider {
  height: 1px;
  background: var(--di-soft-border);
  margin: 10px 0 11px;
}

.di-section-heading {
  min-height: 22px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 7px;
  color: #475467;
  font-size: 10.6px;
  font-weight: 800;
  letter-spacing: 0.045em;
  text-transform: uppercase;
}

.di-soft-note {
  color: #98a2b3;
  font-size: 9.7px;
  font-weight: 650;
  letter-spacing: 0;
  text-transform: none;
}

.di-action-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: stretch;
  gap: 8px;
}

.di-action {
  position: relative;
  min-width: 0;
  min-height: 54px;
  padding: 8px 9px;
  border: 1px solid var(--di-border);
  border-radius: 12px;
  background: #ffffff;
  display: grid;
  grid-template-columns: 26px minmax(0, 1fr) auto;
  align-items: center;
  gap: 7px;
  overflow: hidden;
  color: #344054;
  text-align: left;
  cursor: pointer;
  transition: transform 120ms ease, border-color 120ms ease, box-shadow 120ms ease, background 120ms ease;
}

.di-action:hover:not(:disabled) {
  transform: translateY(-1px);
  border-color: color-mix(in srgb, var(--di-brand) 22%, transparent);
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.07);
  background: #fcfcfe;
}

.di-action:active:not(:disabled) {
  transform: translateY(0) scale(0.995);
}

.di-action:disabled {
  opacity: 0.52;
  cursor: default;
}

.di-action.is-complete {
  border-color: rgba(22, 163, 74, 0.15);
}

.di-action-icon {
  width: 26px;
  height: 26px;
  border-radius: 8px;
  display: inline-grid;
  place-items: center;
  background: color-mix(in srgb, var(--di-brand) 7%, white);
  color: var(--di-brand);
  font-size: 14px;
}

.di-action-label {
  min-width: 0;
  overflow: hidden;
  font-size: 11.7px;
  line-height: 1.25;
  font-weight: 750;
  text-transform: capitalize;
  white-space: normal;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.di-action-complete {
  color: #16a34a;
  font-size: 13px;
}

.di-page-map {
  margin-top: 8px;
  overflow: visible;
  border: 1px solid var(--di-soft-border);
  border-radius: 10px;
  background: #ffffff;
}

.di-page-map-row {
  width: 100%;
  min-height: 46px;
  padding: 8px 9px;
  border: none;
  border-bottom: 1px solid var(--di-soft-border);
  background: transparent;
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr) 18px;
  align-items: center;
  gap: 8px;
  color: inherit;
  cursor: pointer;
  text-align: left;
}

.di-page-map-row:hover:not(:disabled) {
  background: #fafbff;
}

.di-page-map-row.is-selected {
  background: color-mix(in srgb, var(--di-brand) 6%, white);
}

.di-page-map-row:disabled {
  cursor: default;
  opacity: 0.58;
}

.di-page-range {
  color: var(--di-brand);
  font-size: 10.7px;
  line-height: 1.25;
  font-weight: 800;
}

.di-page-label {
  min-width: 0;
  font-size: 11.2px;
  line-height: 1.34;
  white-space: normal;
  overflow: hidden;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.di-page-map-row > svg {
  color: #98a2b3;
}

.di-page-map-more {
  width: 100%;
  min-height: 38px;
  border: none;
  background: #fafbfc;
  color: #475467;
  font: inherit;
  font-size: 10px;
  font-weight: 800;
  cursor: pointer;
}

.di-page-map-more:hover {
  color: var(--di-brand);
  background: #ffffff;
}

.di-page-map-footer {
  display: grid;
  grid-template-columns: 1fr;
}

.di-page-map-footer.has-two {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.di-page-map-footer.has-two .di-page-map-more + .di-page-map-more {
  border-left: 1px solid var(--di-soft-border);
}

.di-empty-note {
  padding: 12px;
  color: var(--di-muted);
  font-size: 11px;
  line-height: 1.45;
}

.di-ask-button {
  width: 100%;
  min-height: 58px;
  margin-top: 10px;
  padding: 8px 10px;
  border: 1px solid color-mix(in srgb, var(--di-brand) 16%, transparent);
  border-radius: 14px;
  background: linear-gradient(135deg, color-mix(in srgb, var(--di-brand) 6%, white), #ffffff 62%);
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) 20px;
  align-items: center;
  gap: 9px;
  text-align: left;
  cursor: pointer;
  color: var(--di-text);
  transition: transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease;
}

.di-ask-button:hover:not(:disabled) {
  transform: translateY(-1px);
  border-color: color-mix(in srgb, var(--di-brand) 30%, transparent);
  box-shadow: 0 10px 24px rgba(30, 42, 120, 0.10);
}

.di-ask-button:disabled {
  cursor: default;
  opacity: 0.64;
}

.di-ask-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: inline-grid;
  place-items: center;
  color: white;
  background: var(--di-brand);
  box-shadow: 0 7px 16px color-mix(in srgb, var(--di-brand) 22%, transparent);
}

.di-ask-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.di-ask-copy strong {
  font-size: 12.3px;
  font-weight: 800;
}

.di-ask-copy small {
  color: var(--di-muted);
  font-size: 10.2px;
  line-height: 1.25;
}

.di-ask-arrow {
  color: var(--di-brand);
}

.di-delete-confirm {
  margin-top: 8px;
  padding: 10px;
  border: 1px solid rgba(180, 35, 24, 0.13);
  border-radius: 12px;
  background: #fffafa;
}

.di-delete-confirm strong {
  display: block;
  color: #7a271a;
  font-size: 11px;
  line-height: 1.3;
}

.di-delete-confirm p {
  margin: 4px 0 9px;
  color: #667085;
  font-size: 9.8px;
  line-height: 1.4;
}

.di-delete-confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 7px;
}

.di-delete-confirm-actions button {
  min-height: 32px;
  padding: 6px 10px;
  border-radius: 9px;
  border: 1px solid var(--di-border);
  background: #ffffff;
  color: #344054;
  font: inherit;
  font-size: 10.5px;
  font-weight: 750;
  cursor: pointer;
}

.di-delete-confirm-actions button.is-danger {
  color: #ffffff;
  background: #b42318;
  border-color: #b42318;
}

.di-delete-confirm-actions button:disabled {
  opacity: 0.56;
  cursor: default;
}

.di-progress-footer {
  margin-top: 11px;
  padding: 10px 11px;
  border: 1px solid var(--di-soft-border);
  border-radius: 13px;
  background: linear-gradient(180deg, #fbfcff, #f7f8fb);
}

.di-progress-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 7px;
}

.di-progress-heading span:first-child {
  min-width: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #475467;
  font-size: 10.2px;
  font-weight: 800;
  letter-spacing: 0.035em;
  text-transform: uppercase;
}

.di-progress-value {
  flex: 0 0 auto;
  color: var(--di-brand);
  font-size: 10.5px;
  font-weight: 800;
}

.di-progress-track {
  width: 100%;
  height: 6px;
  overflow: hidden;
  border-radius: 999px;
  background: #e8ebf1;
}

.di-progress-bar {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, var(--di-brand), #5062d7);
  transition: width 260ms ease;
}

.di-progress-bar.is-indeterminate {
  animation: diProgressSlide 1.15s ease-in-out infinite;
}

.di-progress-copy {
  margin-top: 6px;
  color: #667085;
  font-size: 10.1px;
  line-height: 1.35;
  white-space: normal;
  overflow-wrap: anywhere;
}

.di-inline-footer {
  margin-top: 9px;
  min-height: 22px;
  display: flex;
  align-items: center;
  color: #7b8493;
  font-size: 10px;
}

.di-inline-footer span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.di-inline-footer svg {
  color: #16a34a;
}

.di-edge-rail {
  --di-brand: #1E2A78;
  position: fixed;
  z-index: 1145;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 50px;
  min-width: 50px;
  height: 112px;
  padding: 0 7px;
  border: 1px solid rgba(15, 23, 42, 0.10);
  border-right: none;
  border-radius: 16px 0 0 16px;
  background: rgba(255, 255, 255, 0.97);
  box-shadow: -8px 10px 26px rgba(15, 23, 42, 0.10);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: var(--di-brand);
  cursor: pointer;
  overflow: hidden;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  transition: box-shadow 150ms ease, background 150ms ease, transform 120ms ease;
  -webkit-tap-highlight-color: transparent;
}

.di-edge-rail:hover,
.di-edge-rail:focus-visible {
  width: 50px;
  background: #ffffff;
  box-shadow: -10px 16px 34px rgba(15, 23, 42, 0.14);
}

.di-edge-rail:active {
  transform: translateY(-50%) scale(0.98);
}

.di-edge-line {
  position: absolute;
  right: 0;
  top: 12px;
  bottom: 12px;
  width: 2px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--di-brand) 58%, white);
}

.di-edge-icon {
  font-size: 19px;
  line-height: 1;
}

.di-edge-count {
  min-width: 22px;
  height: 20px;
  border-radius: 999px;
  padding: 0 5px;
  display: inline-grid;
  place-items: center;
  color: #ffffff;
  background: var(--di-brand);
  font-size: 9.5px;
  font-weight: 800;
}

.di-edge-chevron {
  font-size: 13px;
}

.di-edge-mobile {
  top: 44%;
  width: 46px;
  min-width: 46px;
  height: 100px;
  border-radius: 14px 0 0 14px;
  touch-action: manipulation;
}

.di-edge-mobile:hover,
.di-edge-mobile:focus-visible {
  width: 46px;
}

.di-backdrop {
  position: fixed;
  z-index: 1140;
  inset: 0;
  border: none;
  padding: 0;
  margin: 0;
  background: rgba(15, 23, 42, 0.28);
  cursor: default;
  backdrop-filter: blur(1.5px);
  -webkit-backdrop-filter: blur(1.5px);
}

.di-backdrop-tablet {
  background: rgba(15, 23, 42, 0.18);
}

.di-spin {
  animation: diSpin 0.9s linear infinite;
}

.document-intelligence button:focus-visible,
.di-edge-rail:focus-visible,
.di-backdrop:focus-visible {
  outline: 3px solid rgba(37, 99, 235, 0.24);
  outline-offset: 2px;
}

@keyframes diSpin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes diProgressSlide {
  0% { transform: translateX(-140%); }
  50% { transform: translateX(210%); }
  100% { transform: translateX(520%); }
}

@keyframes diSheetIn {
  from { transform: translateY(18px); opacity: 0.92; }
  to { transform: translateY(0); opacity: 1; }
}

@media (max-width: 640px) {
  .di-dock.di-mobile {
    padding-left: max(12px, env(safe-area-inset-left, 0px));
    padding-right: max(12px, env(safe-area-inset-right, 0px));
  }

  .di-mobile .di-title {
    font-size: 15px;
  }

  .di-mobile .di-action-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  .di-mobile .di-action {
    min-height: 50px;
    padding: 8px;
  }

  .di-mobile .di-action-label {
    font-size: 11.4px;
  }

  .di-mobile .di-chat-use-head {
    align-items: stretch;
    flex-direction: column;
    gap: 8px;
  }

  .di-mobile .di-chat-use-segment {
    width: 100%;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .di-mobile .di-document-list {
    max-height: 34dvh;
  }

  .di-mobile .di-view-file-button {
    min-height: 30px;
    padding: 5px 8px;
    font-size: 10px;
  }

  .di-mobile .di-page-range-fields {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .di-mobile .di-page-range-apply {
    grid-column: 1 / -1;
    width: 100%;
  }

  .di-inline.di-mobile {
    padding: 12px;
    border-radius: 16px;
  }
}

@media (max-width: 390px) {
  .di-mobile .di-action-grid {
    gap: 7px;
  }

  .di-mobile .di-action {
    grid-template-columns: 24px minmax(0, 1fr) auto;
    min-height: 47px;
    padding: 7px;
  }

  .di-mobile .di-action-icon {
    width: 24px;
    height: 24px;
  }

  .di-mobile .di-ask-copy small {
    font-size: 9.7px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .document-intelligence *,
  .di-edge-rail,
  .di-floating-shell {
    animation: none !important;
    transition: none !important;
    scroll-behavior: auto !important;
  }
}

@media print {
  .di-floating-shell,
  .di-edge-rail,
  .di-backdrop,
  .di-inline-footer {
    display: none !important;
  }
}
`;
let documentIntelligenceStyleUsers = 0;

const useSafeLayoutEffect =
  typeof window !== "undefined" && typeof document !== "undefined" ? useLayoutEffect : useEffect;

function useDocumentIntelligenceStyles() {
  useSafeLayoutEffect(() => {
    if (typeof document === "undefined") return undefined;

    let styleEl = document.getElementById(DOCUMENT_INTELLIGENCE_STYLE_ID);
    let ownedByComponent = false;

    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = DOCUMENT_INTELLIGENCE_STYLE_ID;
      styleEl.setAttribute("data-asklawly-owned", "true");
      styleEl.textContent = DOCUMENT_INTELLIGENCE_CSS;
      document.head.appendChild(styleEl);
      ownedByComponent = true;
    } else if (styleEl.getAttribute("data-asklawly-owned") === "true") {
      ownedByComponent = true;

      // Hot-reload / code-update safety: keep the mounted style synchronized
      // with the current component source without creating duplicate tags.
      if (styleEl.textContent !== DOCUMENT_INTELLIGENCE_CSS) {
        styleEl.textContent = DOCUMENT_INTELLIGENCE_CSS;
      }
    }

    documentIntelligenceStyleUsers += 1;

    return () => {
      documentIntelligenceStyleUsers = Math.max(0, documentIntelligenceStyleUsers - 1);

      if (documentIntelligenceStyleUsers !== 0 || !ownedByComponent) return;

      const current = document.getElementById(DOCUMENT_INTELLIGENCE_STYLE_ID);
      if (current?.getAttribute("data-asklawly-owned") === "true") {
        current.remove();
      }
    };
  }, []);
}


const DEFAULT_DOCUMENT_ACTIONS = [
  { id: "brief", label: "Brief", icon: "brief" },
  { id: "key_dates", label: "Key Dates", icon: "dates" },
  { id: "parties", label: "Parties", icon: "parties" },
  { id: "page_map", label: "Page Map", icon: "page_map" },
];

const TYPE_ACTIONS = {
  complaint: [
    { id: "brief", label: "Case Brief", icon: "brief" },
    { id: "claims", label: "Claims", icon: "claims" },
    { id: "parties", label: "Parties", icon: "parties" },
    { id: "key_dates", label: "Key Dates", icon: "dates" },
    { id: "damages", label: "Damages", icon: "damages" },
    { id: "page_map", label: "Page Map", icon: "page_map" },
  ],
  petition: [
    { id: "brief", label: "Case Brief", icon: "brief" },
    { id: "issues", label: "Issues", icon: "issues" },
    { id: "parties", label: "Parties", icon: "parties" },
    { id: "key_dates", label: "Key Dates", icon: "dates" },
    { id: "relief", label: "Requested Relief", icon: "claims" },
    { id: "page_map", label: "Page Map", icon: "page_map" },
  ],
  contract: [
    { id: "brief", label: "Summary", icon: "brief" },
    { id: "obligations", label: "Obligations", icon: "obligations" },
    { id: "risks", label: "Risks", icon: "risks" },
    { id: "termination", label: "Termination", icon: "termination" },
    { id: "key_dates", label: "Key Dates", icon: "dates" },
    { id: "page_map", label: "Page Map", icon: "page_map" },
  ],
  agreement: [
    { id: "brief", label: "Summary", icon: "brief" },
    { id: "obligations", label: "Obligations", icon: "obligations" },
    { id: "risks", label: "Risks", icon: "risks" },
    { id: "termination", label: "Termination", icon: "termination" },
    { id: "key_dates", label: "Key Dates", icon: "dates" },
    { id: "page_map", label: "Page Map", icon: "page_map" },
  ],
  order: [
    { id: "holding", label: "Holding", icon: "holding" },
    { id: "orders", label: "Orders", icon: "orders" },
    { id: "key_dates", label: "Deadlines", icon: "dates" },
    { id: "parties", label: "Parties", icon: "parties" },
    { id: "issues", label: "Key Issues", icon: "issues" },
    { id: "page_map", label: "Page Map", icon: "page_map" },
  ],
  notice: [
    { id: "brief", label: "What It Says", icon: "brief" },
    { id: "requirements", label: "What Is Required", icon: "obligations" },
    { id: "key_dates", label: "Deadline", icon: "dates" },
    { id: "issues", label: "Issues", icon: "issues" },
    { id: "next_steps", label: "Response Steps", icon: "orders" },
    { id: "page_map", label: "Page Map", icon: "page_map" },
  ],
  motion: [
    { id: "brief", label: "Motion Brief", icon: "brief" },
    { id: "issues", label: "Issues", icon: "issues" },
    { id: "arguments", label: "Arguments", icon: "claims" },
    { id: "authorities", label: "Authorities", icon: "holding" },
    { id: "key_dates", label: "Key Dates", icon: "dates" },
    { id: "page_map", label: "Page Map", icon: "page_map" },
  ],
};

const ALL_DOCUMENT_ACTIONS = [
  { id: "combined_brief", label: "Combined Brief", icon: "brief" },
  { id: "compare", label: "Compare", icon: "compare" },
  { id: "combined_timeline", label: "Timeline", icon: "dates" },
  { id: "key_issues", label: "Key Issues", icon: "issues" },
];


function getBackendBase(explicitBase = "") {
  const raw = String(
    explicitBase ||
      process.env.REACT_APP_BACKEND_URL ||
      ""
  ).trim();
  return raw.replace(/\/+$/, "");
}

async function getFirebaseIdTokenForDocumentIntelligence() {
  const auth = getAuth();

  const user = auth.currentUser
    ? auth.currentUser
    : await new Promise((resolve, reject) => {
        let unsubscribe = null;
        const timer = setTimeout(() => {
          try {
            unsubscribe?.();
          } catch {}
          reject(new Error("Authentication is not available."));
        }, 3500);

        unsubscribe = onAuthStateChanged(auth, (nextUser) => {
          if (!nextUser) return;
          clearTimeout(timer);
          try {
            unsubscribe?.();
          } catch {}
          resolve(nextUser);
        });
      });

  const token = await user?.getIdToken?.(false);
  if (!token) throw new Error("Authentication is not available.");
  return token;
}

async function readDocumentIntelligenceResponse(response, fallbackMessage) {
  let data = {};
  try {
    data = await response.json();
  } catch {
    data = {};
  }

  if (!response.ok) {
    const error = new Error(
      String(
        data?.message ||
          data?.error ||
          fallbackMessage ||
          `Document Intelligence request failed (${response.status}).`
      )
    );
    error.status = response.status;
    error.code = data?.code || null;
    error.payload = data;
    throw error;
  }

  return data && typeof data === "object" ? data : {};
}

function clampCatalogLimit(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return DEFAULT_CATALOG_PAGE_SIZE;
  return Math.max(1, Math.min(MAX_CATALOG_PAGE_SIZE, Math.floor(parsed)));
}

function mergeDocumentLists(existing, incoming) {
  const result = [];
  const byId = new Map();

  [...(Array.isArray(existing) ? existing : []), ...(Array.isArray(incoming) ? incoming : [])].forEach(
    (item, index) => {
      if (!item || typeof item !== "object") return;
      const id = makeDocumentKey(item, index);
      const previous = byId.get(id);
      const merged = previous ? { ...previous, ...item } : item;
      byId.set(id, merged);
      if (!previous) result.push(id);
    }
  );

  return result.map((id) => byId.get(id)).filter(Boolean);
}

function humanizeStage(value) {
  const text = String(value || "")
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function makeDocumentKey(doc, index = 0) {
  return String(
    doc?.id ||
      doc?.document_id ||
      doc?.documentId ||
      doc?.uploadBatchId ||
      doc?.fileName ||
      doc?.name ||
      `document_${index}`
  );
}

function normalizeStatus(status) {
  const value = String(status || "ready").trim().toLowerCase();

  if (
    [
      "reserved",
      "queued",
      "uploading",
      "uploaded",
      "dispatching",
      "claimed",
      "processing",
      "indexing",
      "analyzing",
    ].includes(value)
  ) {
    return value;
  }

  if (["failed", "rejected", "cancelled", "error"].includes(value)) {
    return "error";
  }

  return "ready";
}

function normalizeDocumentChatMode(value) {
  const mode = String(value || "auto")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  if (["off", "disabled", "do_not_use", "never_use"].includes(mode)) {
    return "off";
  }

  return ["force", "forced", "always", "always_use", "use_always"].includes(mode)
    ? "force"
    : "auto";
}

function toPositivePageNumber(value) {
  if (value === null || value === undefined || value === "" || typeof value === "boolean") {
    return null;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  const page = Math.floor(parsed);
  return page > 0 ? page : null;
}

function pageNumbersForRange(startPage, endPage) {
  const start = toPositivePageNumber(startPage);
  const end = toPositivePageNumber(endPage ?? startPage);
  if (!start || !end) return [];
  const low = Math.min(start, end);
  const high = Math.max(start, end);
  return Array.from({ length: high - low + 1 }, (_, index) => low + index);
}

function normalizeDocumentPageScope(value, pageCount = null) {
  if (!value || typeof value !== "object") return null;

  const listedPages = Array.isArray(value.pageNumbers)
    ? value.pageNumbers
    : Array.isArray(value.page_numbers)
    ? value.page_numbers
    : Array.isArray(value.pages)
    ? value.pages
    : [];

  let pageNumbers = Array.from(
    new Set(listedPages.map(toPositivePageNumber).filter(Boolean))
  ).sort((a, b) => a - b);

  let startPage =
    toPositivePageNumber(value.startPage) ||
    toPositivePageNumber(value.start_page) ||
    toPositivePageNumber(value.pageStart) ||
    toPositivePageNumber(value.page_start) ||
    toPositivePageNumber(value.page);

  let endPage =
    toPositivePageNumber(value.endPage) ||
    toPositivePageNumber(value.end_page) ||
    toPositivePageNumber(value.pageEnd) ||
    toPositivePageNumber(value.page_end);

  if (!pageNumbers.length && startPage) {
    pageNumbers = pageNumbersForRange(startPage, endPage || startPage);
  }

  if (pageNumbers.length) {
    startPage = pageNumbers[0];
    endPage = pageNumbers[pageNumbers.length - 1];
  }

  if (!startPage) return null;
  endPage = endPage || startPage;

  const maxPage = toPositivePageNumber(pageCount);
  if (maxPage) {
    pageNumbers = pageNumbers.filter((page) => page <= maxPage);
    if (!pageNumbers.length) return null;
    startPage = pageNumbers[0];
    endPage = pageNumbers[pageNumbers.length - 1];
  }

  const label = String(
    value.label ||
      value.title ||
      value.heading ||
      (startPage === endPage
        ? `Page ${startPage}`
        : `Pages ${startPage}–${endPage}`)
  ).trim();

  return {
    startPage,
    endPage,
    pageNumbers,
    label,
    source: String(value.source || value.scopeSource || value.scope_source || "page_scope"),
  };
}

function pageScopeRangeText(scope) {
  const normalized = normalizeDocumentPageScope(scope);
  if (!normalized) return "";
  return normalized.startPage === normalized.endPage
    ? `Page ${normalized.startPage}`
    : `Pages ${normalized.startPage}–${normalized.endPage}`;
}

function pageScopesEqual(left, right) {
  const a = normalizeDocumentPageScope(left);
  const b = normalizeDocumentPageScope(right);
  if (!a || !b) return !a && !b;
  return (
    a.startPage === b.startPage &&
    a.endPage === b.endPage &&
    a.pageNumbers.join(",") === b.pageNumbers.join(",")
  );
}


function normalizeDocument(doc, index = 0) {
  const source = doc && typeof doc === "object" ? doc : {};
  const id = makeDocumentKey(source, index);
  const name = String(
    source.name ||
      source.fileName ||
      source.filename ||
      source.title ||
      `Document ${index + 1}`
  );

  const pageCountRaw =
    source.pageCount ??
    source.page_count ??
    source.pages ??
    source.totalPages ??
    source.total_pages;
  const pageCount = Number.isFinite(Number(pageCountRaw))
    ? Math.max(0, Number(pageCountRaw))
    : null;

  const type = String(
    source.type ||
      source.documentType ||
      source.document_type ||
      "document"
  )
    .trim()
    .toLowerCase();

  const status = normalizeStatus(
    source.status ||
      source.documentStatus ||
      source.document_status ||
      source.indexStatus ||
      source.index_status ||
      "ready"
  );

  const hasExplicitWorkerActions =
    Array.isArray(source.documentActions) ||
    Array.isArray(source.document_actions);

  const rawActions = Array.isArray(source.documentActions)
    ? source.documentActions
    : Array.isArray(source.document_actions)
    ? source.document_actions
    : Array.isArray(source.actions)
    ? source.actions
    : null;

  // If Main explicitly supplied documentActions (including an empty list), it
  // is authoritative. Generic fallback actions are only for older parent-driven
  // documents that predate the worker action manifest.
  const actions = rawActions === null ? null : rawActions;

  const completedActions = new Set(
    Array.isArray(source.completedActions)
      ? source.completedActions.map((x) => String(x))
      : Array.isArray(source.completed_actions)
      ? source.completed_actions.map((x) => String(x))
      : []
  );

  const pageMap = Array.isArray(source.pageMap)
    ? source.pageMap
    : Array.isArray(source.page_map)
    ? source.page_map
    : [];

  const progress =
    source.progress && typeof source.progress === "object"
      ? source.progress
      : {};

  const progressStage = String(
    source.progressStage ||
      source.progress_stage ||
      progress.stage ||
      ""
  )
    .trim()
    .toLowerCase();

  const progressPercentRaw =
    source.progressPercent ??
    source.progress_percent ??
    progress.percent ??
    null;

  const hasProgressPercent =
    progressPercentRaw !== null &&
    progressPercentRaw !== undefined &&
    String(progressPercentRaw).trim() !== "";

  const parsedProgressPercent = hasProgressPercent
    ? Number(progressPercentRaw)
    : Number.NaN;

  const progressPercent = Number.isFinite(parsedProgressPercent)
    ? Math.max(0, Math.min(100, parsedProgressPercent))
    : null;

  const progressMessage = String(
    source.progressMessage ||
      source.progress_message ||
      progress.message ||
      source.statusMessage ||
      ""
  ).trim();

  const artifacts = Array.isArray(source.artifacts)
    ? source.artifacts
    : [];

  return {
    ...source,
    id,
    name,
    pageCount,
    type,
    status,
    actions,
    workerActionsProvided: hasExplicitWorkerActions,
    completedActions,
    pageMap,
    artifacts,
    progressStage,
    progressPercent,
    progressMessage,
  };
}

function normalizeAction(action, index = 0) {
  if (typeof action === "string") {
    return { id: action, label: action.replace(/[_-]+/g, " "), icon: action };
  }
  const source = action && typeof action === "object" ? action : {};
  const id = String(
    source.id ||
      source.actionId ||
      source.action_id ||
      source.key ||
      source.action ||
      `action_${index}`
  );
  return {
    ...source,
    id,
    actionId: id,
    label: String(
      source.label ||
        source.title ||
        id.replace(/[_-]+/g, " ")
    ),
    description: String(source.description || ""),
    icon: source.icon || id,
  };
}

function isAskDocumentComposerAction(action) {
  const source =
    action && typeof action === "object"
      ? action
      : {};

  const normalizedId = String(
    source.id ||
      source.actionId ||
      source.action_id ||
      ""
  )
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  if (
    new Set([
      "ask_document",
      "ask_this_document",
      "ask_about_document",
      "ask_about_this_document",
      "chat_with_document",
      "question_document",
    ]).has(normalizedId)
  ) {
    return true;
  }

  const normalizedLabel = String(
    source.label ||
      source.title ||
      ""
  )
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

  return new Set([
    "ask document",
    "ask this document",
    "ask about document",
    "ask about this document",
    "chat with document",
  ]).has(normalizedLabel);
}

function actionIcon(name) {
  const key = String(name || "").toLowerCase();
  if (key.includes("date") || key.includes("timeline")) return <FiCalendar />;
  if (key.includes("part")) return <FiUsers />;
  if (key.includes("damage") || key.includes("money") || key.includes("amount")) return <FiDollarSign />;
  if (key.includes("map") || key.includes("page")) return <FiMap />;
  if (key.includes("risk")) return <FiShield />;
  if (key.includes("claim") || key.includes("argument") || key.includes("relief")) return <FiActivity />;
  if (key.includes("hold") || key.includes("authorit")) return <FiBookOpen />;
  if (key.includes("order") || key.includes("step")) return <FiList />;
  if (key.includes("obligation") || key.includes("requirement")) return <FiCheckCircle />;
  if (key.includes("terminat")) return <FiX />;
  if (key.includes("compare")) return <FiLayers />;
  if (key.includes("issue")) return <FiZap />;
  if (key.includes("search")) return <FiSearch />;
  return <FiFileText />;
}

function statusMeta(status) {
  switch (normalizeStatus(status)) {
    case "reserved":
      return { label: "Reserved", icon: <FiClock />, tone: "working" };
    case "uploaded":
      return { label: "Uploaded", icon: <FiCheckCircle />, tone: "working" };
    case "dispatching":
      return { label: "Starting", icon: <FiRefreshCw className="di-spin" />, tone: "working" };
    case "claimed":
      return { label: "Starting", icon: <FiRefreshCw className="di-spin" />, tone: "working" };
    case "uploading":
      return { label: "Uploading", icon: <FiRefreshCw className="di-spin" />, tone: "working" };
    case "queued":
      return { label: "Queued", icon: <FiClock />, tone: "working" };
    case "processing":
      return { label: "Processing", icon: <FiRefreshCw className="di-spin" />, tone: "working" };
    case "indexing":
      return { label: "Indexing", icon: <FiRefreshCw className="di-spin" />, tone: "working" };
    case "analyzing":
      return { label: "Analyzing", icon: <FiRefreshCw className="di-spin" />, tone: "working" };
    case "error":
      return { label: "Needs attention", icon: <FiAlertTriangle />, tone: "error" };
    default:
      return { label: "Ready", icon: <FiCheckCircle />, tone: "ready" };
  }
}

function useViewportClass() {
  const getMode = useCallback(() => {
    try {
      const width = window.visualViewport?.width || window.innerWidth || 1280;
      if (width <= MOBILE_BREAKPOINT) return "mobile";
      if (width <= TABLET_BREAKPOINT) return "tablet";
      return "desktop";
    } catch {
      return "desktop";
    }
  }, []);

  const [mode, setMode] = useState(getMode);

  useEffect(() => {
    const update = () => setMode(getMode());
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    vv?.addEventListener?.("resize", update);
    vv?.addEventListener?.("scroll", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
      vv?.removeEventListener?.("resize", update);
      vv?.removeEventListener?.("scroll", update);
    };
  }, [getMode]);

  return mode;
}

function DocumentStatus({ status }) {
  const meta = statusMeta(status);
  return (
    <span className={`di-status di-status-${meta.tone}`}>
      <span aria-hidden="true">{meta.icon}</span>
      {meta.label}
    </span>
  );
}

function PageMap({
  items,
  onSelect,
  pageCount,
  visibleCount = PAGE_SECTION_INITIAL_VISIBLE,
  onShowMore,
  onShowLess,
  selectedScope,
  disabled = false,
}) {
  if (!Array.isArray(items) || !items.length) {
    return null;
  }

  const normalizedItems = items
    .map((item, index) => {
      const source = item && typeof item === "object" ? item : {};
      const startPage =
        source.startPage ??
        source.start_page ??
        source.page ??
        source.pageStart ??
        source.page_start;
      const endPage =
        source.endPage ??
        source.end_page ??
        source.pageEnd ??
        source.page_end ??
        startPage;
      const label = String(
        source.label ||
          source.title ||
          source.heading ||
          source.summary ||
          `Section ${index + 1}`
      );
      const scope = normalizeDocumentPageScope(
        {
          ...source,
          startPage,
          endPage,
          label,
          source: "page_map",
        },
        pageCount
      );
      return scope ? { source, scope, label } : null;
    })
    .filter(Boolean);

  if (!normalizedItems.length) return null;

  const shown = normalizedItems.slice(
    0,
    Math.max(PAGE_SECTION_INITIAL_VISIBLE, Number(visibleCount) || 0)
  );
  const hasMore = shown.length < normalizedItems.length;
  const canShowLess = shown.length > PAGE_SECTION_INITIAL_VISIBLE;

  return (
    <div className="di-page-map" role="list" aria-label="Document sections">
      {shown.map(({ source, scope, label }, index) => {
        const selected = pageScopesEqual(scope, selectedScope);
        return (
          <button
            key={`${scope.startPage}-${scope.endPage}-${label}-${index}`}
            type="button"
            className={`di-page-map-row ${selected ? "is-selected" : ""}`}
            onClick={() => onSelect?.(source, scope)}
            disabled={disabled}
            aria-pressed={selected}
          >
            <span className="di-page-range">
              {scope.startPage === scope.endPage
                ? `${scope.startPage}`
                : `${scope.startPage}–${scope.endPage}`}
            </span>
            <span className="di-page-label">{label}</span>
            {selected ? (
              <FiCheck aria-label="Selected section" />
            ) : (
              <FiChevronsRight aria-hidden="true" />
            )}
          </button>
        );
      })}
      {hasMore || canShowLess ? (
        <div className={`di-page-map-footer ${hasMore && canShowLess ? "has-two" : ""}`}>
          {canShowLess ? (
            <button type="button" className="di-page-map-more" onClick={onShowLess}>
              Show fewer
            </button>
          ) : null}
          {hasMore ? (
            <button type="button" className="di-page-map-more" onClick={onShowMore}>
              Show {Math.min(PAGE_SECTION_REVEAL_STEP, normalizedItems.length - shown.length)} more
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default function DocumentIntelligence({
  documents = [],
  presentation = "dock",
  open,
  defaultOpen = true,
  onOpenChange,
  activeDocumentId,
  defaultActiveDocumentId,
  onActiveDocumentChange,
  documentChatMode,
  defaultDocumentChatMode = "auto",
  onDocumentChatModeChange,
  documentPageScope,
  defaultDocumentPageScope = null,
  onDocumentPageScopeChange,
  allowAllDocuments = true,
  allDocumentsSelected = false,
  onAllDocumentsChange,
  onAction,
  onAskDocument,
  onOpenDocument,
  onRemoveDocument,
  onDeleteDocument,
  onDocumentUnavailable,
  onRefreshAction,
  onPageMapSelect,
  actionState = {},
  pageMapMode = "inline",
  title = "Document Intelligence",
  brandColor = BRAND,
  persistKey = null,
  className = "",
  style = null,
  showInlineStatus = true,

  // Reusable dock/catalog contract.
  backendBase,
  sessionId,
  consumer,
  autoLoadCatalog,
  catalogLimit = DEFAULT_CATALOG_PAGE_SIZE,
  catalogRefreshKey,
  authTokenProvider,
  onCatalogChange,
  showProgress = true,
}) {
  useDocumentIntelligenceStyles();

  const viewport = useViewportClass();
  const apiBase = useMemo(
    () => getBackendBase(backendBase),
    [backendBase]
  );
  const resolvedCatalogLimit = useMemo(
    () => clampCatalogLimit(catalogLimit),
    [catalogLimit]
  );

  // Dock is self-managed by default; inline remains intentionally parent-driven
  // so a message-level control never turns into the owner's full inventory.
  const catalogEnabled =
    typeof autoLoadCatalog === "boolean"
      ? autoLoadCatalog
      : presentation === "dock";

  const [catalogDocuments, setCatalogDocuments] = useState([]);
  const [catalogPagination, setCatalogPagination] = useState({
    limit: resolvedCatalogLimit,
    hasMore: false,
    nextCursor: null,
  });
  const [catalogLoadingMore, setCatalogLoadingMore] = useState(false);
  const [catalogError, setCatalogError] = useState("");
  const [documentDetails, setDocumentDetails] = useState({});
  const [deleteConfirmId, setDeleteConfirmId] = useState("");
  const [deleteBusyId, setDeleteBusyId] = useState("");

  const catalogRequestSequenceRef = useRef(0);
  const detailRequestIdsRef = useRef(new Set());

  const getIdToken = useCallback(async () => {
    if (typeof authTokenProvider === "function") {
      const token = await authTokenProvider();
      if (!token) throw new Error("Authentication is not available.");
      return token;
    }
    return getFirebaseIdTokenForDocumentIntelligence();
  }, [authTokenProvider]);

  const catalogSourceDocuments = catalogEnabled
    ? catalogDocuments
    : Array.isArray(documents)
    ? documents
    : [];

  const normalizedDocuments = useMemo(() => {
    return (Array.isArray(catalogSourceDocuments)
      ? catalogSourceDocuments
      : []
    ).map((doc, index) => {
      const id = makeDocumentKey(doc, index);
      const detail =
        documentDetails[id] &&
        typeof documentDetails[id] === "object"
          ? documentDetails[id]
          : null;
      return normalizeDocument(
        detail ? { ...doc, ...detail } : doc,
        index
      );
    });
  }, [catalogSourceDocuments, documentDetails]);

  const isControlledOpen = typeof open === "boolean";
  const [internalOpen, setInternalOpen] = useState(() => {
    if (isControlledOpen) return open;
    if (persistKey) {
      try {
        const stored = localStorage.getItem(`${persistKey}:open`);
        if (stored === "1") return true;
        if (stored === "0") return false;
      } catch {}
    }
    return !!defaultOpen;
  });

  const actualOpen = isControlledOpen ? !!open : internalOpen;

  const setOpenState = useCallback(
    (next) => {
      const value = !!next;
      if (!isControlledOpen) setInternalOpen(value);
      if (persistKey) {
        try {
          localStorage.setItem(
            `${persistKey}:open`,
            value ? "1" : "0"
          );
        } catch {}
      }
      onOpenChange?.(value);
    },
    [isControlledOpen, onOpenChange, persistKey]
  );

  const cancelHoverOpen = useCallback(() => {
    if (hoverOpenTimerRef.current) {
      clearTimeout(hoverOpenTimerRef.current);
      hoverOpenTimerRef.current = null;
    }
  }, []);

  const scheduleHoverOpen = useCallback(() => {
    if (viewport === "mobile" || actualOpen) return;

    cancelHoverOpen();
    hoverOpenTimerRef.current = setTimeout(() => {
      hoverOpenTimerRef.current = null;
      setOpenState(true);
    }, DOCK_HOVER_OPEN_DELAY_MS);
  }, [
    actualOpen,
    cancelHoverOpen,
    setOpenState,
    viewport,
  ]);

  useEffect(() => {
    return () => {
      cancelHoverOpen();
      if (dragAnimationFrameRef.current) {
        cancelAnimationFrame(dragAnimationFrameRef.current);
        dragAnimationFrameRef.current = null;
      }
    };
  }, [cancelHoverOpen]);

  const controlledActive =
    activeDocumentId !== undefined &&
    activeDocumentId !== null;

  // Latest backend document is the default. We intentionally do not restore an
  // older persisted document selection on a fresh mount.
  const [internalActiveId, setInternalActiveId] = useState(() =>
    defaultActiveDocumentId
      ? String(defaultActiveDocumentId)
      : ""
  );

  const actualActiveId = controlledActive
    ? String(activeDocumentId || "")
    : internalActiveId;

  const exactActiveDocument =
    normalizedDocuments.find(
      (doc) => doc.id === actualActiveId
    ) || null;

  const activeDocument = controlledActive
    ? exactActiveDocument
    : exactActiveDocument ||
      normalizedDocuments[0] ||
      null;

  // "Auto" is the safe default. The parent may control this state (ChatInterface)
  // while reusable consumers can still use the component standalone.
  const controlledDocumentChatMode =
    documentChatMode !== undefined &&
    documentChatMode !== null;

  const [internalDocumentChatMode, setInternalDocumentChatMode] = useState(() =>
    normalizeDocumentChatMode(defaultDocumentChatMode)
  );

  const actualDocumentChatMode = controlledDocumentChatMode
    ? normalizeDocumentChatMode(documentChatMode)
    : internalDocumentChatMode;

  const chooseDocumentChatMode = useCallback(
    (nextMode) => {
      const normalizedMode = normalizeDocumentChatMode(nextMode);
      if (!controlledDocumentChatMode) {
        setInternalDocumentChatMode(normalizedMode);
      }
      onDocumentChatModeChange?.(normalizedMode, activeDocument);
    },
    [
      activeDocument,
      controlledDocumentChatMode,
      onDocumentChatModeChange,
    ]
  );

  const controlledDocumentPageScope =
    documentPageScope !== undefined;

  const [internalDocumentPageScope, setInternalDocumentPageScope] = useState(() =>
    normalizeDocumentPageScope(defaultDocumentPageScope)
  );

  const actualDocumentPageScope = useMemo(
    () =>
      normalizeDocumentPageScope(
        controlledDocumentPageScope
          ? documentPageScope
          : internalDocumentPageScope,
        activeDocument?.pageCount
      ),
    [
      activeDocument?.pageCount,
      controlledDocumentPageScope,
      documentPageScope,
      internalDocumentPageScope,
    ]
  );

  const chooseDocumentPageScope = useCallback(
    (nextScope, meta = {}) => {
      const normalizedScope = normalizeDocumentPageScope(
        nextScope,
        activeDocument?.pageCount
      );
      if (!controlledDocumentPageScope) {
        setInternalDocumentPageScope(normalizedScope);
      }
      onDocumentPageScopeChange?.(
        normalizedScope,
        activeDocument,
        meta
      );
      return normalizedScope;
    },
    [
      activeDocument,
      controlledDocumentPageScope,
      onDocumentPageScopeChange,
    ]
  );

  const chooseDocument = useCallback(
    (doc) => {
      if (!doc) return;
      if (!controlledActive) {
        setInternalActiveId(doc.id);
      }
      // In controlled mode the parent owns persistence (ChatInterface keeps
      // the selection per chat thread). Only uncontrolled consumers persist
      // their own local choice here.
      if (!controlledActive && persistKey) {
        try {
          localStorage.setItem(
            `${persistKey}:activeDocument`,
            doc.id
          );
        } catch {}
      }
      onAllDocumentsChange?.(false);
      onActiveDocumentChange?.(doc.id, doc);
    },
    [
      controlledActive,
      onActiveDocumentChange,
      onAllDocumentsChange,
      persistKey,
    ]
  );

  useEffect(() => {
    if (!normalizedDocuments.length) {
      if (!controlledActive) {
        setInternalActiveId("");
      }
      return;
    }

    // Controlled mode is authoritative. ChatInterface owns newest-first
    // initialization, per-thread persistence, refresh restoration and fallback
    // after delete/hide. Do not manufacture a different selection here.
    if (controlledActive) {
      return;
    }

    if (
      internalActiveId &&
      normalizedDocuments.some(
        (doc) => doc.id === internalActiveId
      )
    ) {
      return;
    }

    const requestedDefault =
      defaultActiveDocumentId &&
      normalizedDocuments.find(
        (doc) =>
          doc.id === String(defaultActiveDocumentId)
      );

    const fallback =
      requestedDefault || normalizedDocuments[0];

    if (fallback?.id) {
      setInternalActiveId(fallback.id);
      onActiveDocumentChange?.(
        fallback.id,
        fallback
      );
    }
  }, [
    controlledActive,
    defaultActiveDocumentId,
    internalActiveId,
    normalizedDocuments,
    onActiveDocumentChange,
  ]);

  const fetchCatalogPage = useCallback(
    async ({ cursor = null, append = false } = {}) => {
      if (!catalogEnabled) return;
      if (!apiBase) {
        setCatalogError(
          "Document Intelligence is temporarily unavailable."
        );
        return;
      }

      const requestNumber =
        ++catalogRequestSequenceRef.current;

      if (append) {
        setCatalogLoadingMore(true);
      } else {
        setCatalogError("");
      }

      try {
        const token = await getIdToken();
        const params = new URLSearchParams();
        params.set(
          "limit",
          String(resolvedCatalogLimit)
        );
        if (cursor) params.set("cursor", cursor);
        if (sessionId) {
          params.set("session_id", String(sessionId));
        }
        if (consumer) {
          params.set("consumer", String(consumer));
        }

        const response = await fetch(
          `${apiBase}/document-intelligence/documents?${params.toString()}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const payload =
          await readDocumentIntelligenceResponse(
            response,
            "Unable to load Document Intelligence documents."
          );

        if (
          requestNumber !==
          catalogRequestSequenceRef.current
        ) {
          return;
        }

        const incoming = Array.isArray(
          payload?.documents
        )
          ? payload.documents
          : [];
        const nextPagination =
          payload?.pagination &&
          typeof payload.pagination === "object"
            ? payload.pagination
            : {};

        setCatalogDocuments((previous) => {
          const next = append
            ? mergeDocumentLists(previous, incoming)
            : incoming;

          onCatalogChange?.(next, {
            limit: Number(
              nextPagination.limit ||
                resolvedCatalogLimit
            ),
            hasMore: !!nextPagination.hasMore,
            nextCursor:
              nextPagination.nextCursor || null,
          });

          return next;
        });

        setCatalogPagination({
          limit: Number(
            nextPagination.limit ||
              resolvedCatalogLimit
          ),
          hasMore: !!nextPagination.hasMore,
          nextCursor:
            nextPagination.nextCursor || null,
        });
        setCatalogError("");
      } catch (error) {
        if (
          requestNumber !==
          catalogRequestSequenceRef.current
        ) {
          return;
        }
        console.warn(
          "[DocumentIntelligence] catalog load failed:",
          error
        );
        setCatalogError(
          "We couldn't load your documents right now. Please try again."
        );
      } finally {
        if (
          requestNumber ===
          catalogRequestSequenceRef.current
        ) {
          setCatalogLoadingMore(false);
        }
      }
    },
    [
      apiBase,
      catalogEnabled,
      consumer,
      getIdToken,
      onCatalogChange,
      resolvedCatalogLimit,
      sessionId,
    ]
  );

  useEffect(() => {
    if (!catalogEnabled) return;
    fetchCatalogPage({
      cursor: null,
      append: false,
    });
  }, [
    catalogEnabled,
    catalogRefreshKey,
    fetchCatalogPage,
  ]);

  const mergeDetail = useCallback(
    (documentId, patch) => {
      if (!documentId || !patch) return;

      setDocumentDetails((previous) => ({
        ...previous,
        [documentId]: {
          ...(previous[documentId] || {}),
          ...patch,
        },
      }));

      setCatalogDocuments((previous) =>
        previous.map((doc, index) =>
          makeDocumentKey(doc, index) === documentId
            ? { ...doc, ...patch }
            : doc
        )
      );
    },
    []
  );

  const removeCatalogDocument = useCallback(
    (documentId) => {
      if (!documentId) return;

      setCatalogDocuments((previous) =>
        previous.filter(
          (doc, index) =>
            makeDocumentKey(doc, index) !==
            documentId
        )
      );

      setDocumentDetails((previous) => {
        const next = { ...previous };
        delete next[documentId];
        return next;
      });

      if (
        !controlledActive &&
        internalActiveId === documentId
      ) {
        setInternalActiveId("");

        if (persistKey) {
          try {
            const storageKey =
              `${persistKey}:activeDocument`;
            const storedId = String(
              localStorage.getItem(storageKey) || ""
            ).trim();

            if (storedId === String(documentId)) {
              localStorage.removeItem(storageKey);
            }
          } catch {}
        }
      }
    },
    [
      controlledActive,
      internalActiveId,
      persistKey,
    ]
  );

  const notifyDocumentUnavailable = useCallback(
    (documentId, reason = "document_not_found") => {
      const id = String(documentId || "").trim();
      if (!id) return;

      const knownDocument =
        normalizedDocuments.find(
          (document) =>
            String(document?.id || "") === id
        ) || {
          id,
          documentId: id,
        };

      const payload = {
        ...knownDocument,
        id,
        documentId: id,
      };

      const meta = {
        reason,
        source: "document_intelligence",
      };

      // New explicit contract first. Existing ChatInterface already supplies
      // onDeleteDocument, so keep that as a compatibility reconciliation hook.
      if (
        typeof onDocumentUnavailable === "function"
      ) {
        onDocumentUnavailable(payload, meta);
        return;
      }

      if (
        typeof onDeleteDocument === "function"
      ) {
        onDeleteDocument(payload, meta);
      }
    },
    [
      normalizedDocuments,
      onDeleteDocument,
      onDocumentUnavailable,
    ]
  );

  const loadDocumentDetail = useCallback(
    async (documentId, { force = false } = {}) => {
      if (
        !catalogEnabled ||
        !documentId ||
        !apiBase
      ) {
        return null;
      }

      if (
        !force &&
        documentDetails[documentId]?.documentActions
      ) {
        return documentDetails[documentId];
      }

      if (
        detailRequestIdsRef.current.has(documentId)
      ) {
        return null;
      }

      detailRequestIdsRef.current.add(documentId);

      try {
        const token = await getIdToken();
        const response = await fetch(
          `${apiBase}/document-intelligence/documents/${encodeURIComponent(
            documentId
          )}?include_completion_message=false`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const payload =
          await readDocumentIntelligenceResponse(
            response,
            "Unable to load document intelligence."
          );
        const projection =
          payload?.document &&
          typeof payload.document === "object"
            ? payload.document
            : null;

        if (projection) {
          mergeDetail(documentId, projection);
        }
        return projection;
      } catch (error) {
        if (Number(error?.status) === 404) {
          // The panel API keeps abandoned terminal failures visible. Therefore
          // a 404 here is authoritative missing/soft-deleted state, not a
          // failed document that should remain selectable in the panel.
          removeCatalogDocument(documentId);
          notifyDocumentUnavailable(
            documentId,
            error?.code || "document_not_found"
          );
          return null;
        }

        console.warn(
          "[DocumentIntelligence] document detail load failed:",
          error
        );
        return null;
      } finally {
        detailRequestIdsRef.current.delete(
          documentId
        );
      }
    },
    [
      apiBase,
      catalogEnabled,
      documentDetails,
      getIdToken,
      mergeDetail,
      notifyDocumentUnavailable,
      removeCatalogDocument,
    ]
  );

  // Changing the dropdown changes documentId and loads that document's exact
  // actions, Page Map, artifact inventory, and status projection.
  useEffect(() => {
    if (
      !catalogEnabled ||
      !activeDocument?.id
    ) {
      return;
    }

    loadDocumentDetail(activeDocument.id);
  }, [
    activeDocument?.id,
    catalogEnabled,
    loadDocumentDetail,
  ]);

  // Live status polling is intentionally scoped to the ACTIVE document. The
  // backend remains the single source of truth for stage/percent/message.
  useEffect(() => {
    if (
      !catalogEnabled ||
      !activeDocument?.id ||
      !apiBase
    ) {
      return undefined;
    }

    const activeId = activeDocument.id;
    const tone = statusMeta(
      activeDocument.status
    ).tone;

    if (tone !== "working") {
      return undefined;
    }

    let cancelled = false;
    let timer = null;

    const poll = async () => {
      if (cancelled) return;

      try {
        const token = await getIdToken();
        const response = await fetch(
          `${apiBase}/document-intelligence/documents/${encodeURIComponent(
            activeId
          )}/status`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const payload =
          await readDocumentIntelligenceResponse(
            response,
            "Unable to read document status."
          );
        const patch =
          payload?.document &&
          typeof payload.document === "object"
            ? payload.document
            : null;

        if (patch && !cancelled) {
          mergeDetail(activeId, patch);
          const nextTone = statusMeta(
            patch.status
          ).tone;
          if (nextTone === "ready") {
            await loadDocumentDetail(
              activeId,
              { force: true }
            );
            return;
          }
          if (nextTone === "error") {
            return;
          }
        }
      } catch (error) {
        if (Number(error?.status) === 404) {
          removeCatalogDocument(activeId);
          notifyDocumentUnavailable(
            activeId,
            error?.code || "document_not_found"
          );
          return;
        }
        console.warn(
          "[DocumentIntelligence] status poll failed:",
          error
        );
      }

      if (!cancelled) {
        timer = setTimeout(
          poll,
          STATUS_POLL_INTERVAL_MS
        );
      }
    };

    timer = setTimeout(
      poll,
      STATUS_POLL_INTERVAL_MS
    );

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [
    activeDocument?.id,
    activeDocument?.status,
    apiBase,
    catalogEnabled,
    getIdToken,
    loadDocumentDetail,
    mergeDetail,
    notifyDocumentUnavailable,
    removeCatalogDocument,
  ]);

  const handleLoadMore = useCallback(async () => {
    if (
      !catalogPagination.hasMore ||
      !catalogPagination.nextCursor ||
      catalogLoadingMore
    ) {
      return;
    }

    await fetchCatalogPage({
      cursor: catalogPagination.nextCursor,
      append: true,
    });
  }, [
    catalogLoadingMore,
    catalogPagination.hasMore,
    catalogPagination.nextCursor,
    fetchCatalogPage,
  ]);

  const [documentListOpen, setDocumentListOpen] =
    useState(false);
  const [pageMapOpen, setPageMapOpen] =
    useState(false);
  const [visibleSectionCount, setVisibleSectionCount] =
    useState(PAGE_SECTION_INITIAL_VISIBLE);
  const [pageRangeStart, setPageRangeStart] =
    useState("");
  const [pageRangeEnd, setPageRangeEnd] =
    useState("");
  const [pageRangeError, setPageRangeError] =
    useState("");
  const [overflowMenuOpen, setOverflowMenuOpen] =
    useState(false);
  const rootRef = useRef(null);

  // Desktop/tablet dock remains pinned to the right edge but may be moved
  // vertically. The saved value is scoped to the consumer's existing persistKey.
  const [dockTopPx, setDockTopPx] = useState(null);
  const [dockDragging, setDockDragging] = useState(false);
  const [dockViewportMetrics, setDockViewportMetrics] = useState(() => {
    try {
      const vv = window.visualViewport;
      return {
        top: Math.max(0, Number(vv?.offsetTop || 0)),
        height: Math.max(
          1,
          Number(vv?.height || window.innerHeight || 900)
        ),
      };
    } catch {
      return { top: 0, height: 900 };
    }
  });
  const dragStateRef = useRef(null);
  const dragAnimationFrameRef = useRef(null);
  const hoverOpenTimerRef = useRef(null);

  const dockTopStorageKey = useMemo(
    () => (persistKey ? `${persistKey}:dockTopPx` : null),
    [persistKey]
  );

  const getDefaultDockTop = useCallback(() => {
    const baseTop =
      viewport === "tablet" ? 68 : 76;
    const visibleTop =
      Math.max(
        0,
        Number(dockViewportMetrics.top || 0)
      );

    return Math.max(
      baseTop,
      visibleTop + 12
    );
  }, [
    dockViewportMetrics.top,
    viewport,
  ]);

  const getDockVisibleBottom = useCallback(() => {
    const visibleTop =
      Math.max(
        0,
        Number(dockViewportMetrics.top || 0)
      );
    const visibleHeight =
      Math.max(
        1,
        Number(dockViewportMetrics.height || 900)
      );

    return visibleTop + visibleHeight;
  }, [
    dockViewportMetrics.height,
    dockViewportMetrics.top,
  ]);

  const clampDockTop = useCallback(
    (candidate) => {
      const defaultTop = getDefaultDockTop();
      const visibleBottom =
        getDockVisibleBottom();
      const safeBottom =
        visibleBottom - DOCK_BOTTOM_SAFETY_PX;

      // Preserve enough usable panel height while still allowing the user to
      // drag the dock substantially downward.
      const maxTop = Math.max(
        defaultTop,
        safeBottom -
          DOCK_MIN_VISIBLE_HEIGHT_PX
      );

      const numeric = Number(candidate);
      const requested =
        Number.isFinite(numeric)
          ? numeric
          : defaultTop;

      return Math.max(
        defaultTop,
        Math.min(maxTop, requested)
      );
    },
    [
      getDefaultDockTop,
      getDockVisibleBottom,
    ]
  );

  const dockAvailableHeightPx = useMemo(() => {
    if (
      viewport === "mobile" ||
      dockTopPx === null ||
      dockTopPx === undefined
    ) {
      return null;
    }

    const available =
      getDockVisibleBottom() -
      DOCK_BOTTOM_SAFETY_PX -
      dockTopPx;

    return Math.max(
      DOCK_MIN_VISIBLE_HEIGHT_PX,
      Math.floor(available)
    );
  }, [
    dockTopPx,
    getDockVisibleBottom,
    viewport,
  ]);

  const persistDockTop = useCallback(
    (value) => {
      if (!dockTopStorageKey) return;
      try {
        localStorage.setItem(dockTopStorageKey, String(Math.round(value)));
      } catch {}
    },
    [dockTopStorageKey]
  );

  const beginVerticalDrag = useCallback(
    (event) => {
      if (presentation !== "dock" || viewport === "mobile") return;
      if (event.button !== undefined && event.button !== 0) return;

      event.preventDefault();
      event.stopPropagation();

      const currentTop =
        dockTopPx !== null && dockTopPx !== undefined
          ? dockTopPx
          : rootRef.current?.getBoundingClientRect?.().top ||
            getDefaultDockTop();

      dragStateRef.current = {
        pointerId: event.pointerId,
        startY: event.clientY,
        startTop: currentTop,
        latestY: event.clientY,
      };
      setDockDragging(true);

      try {
        event.currentTarget?.setPointerCapture?.(event.pointerId);
      } catch {}
    },
    [
      dockTopPx,
      getDefaultDockTop,
      presentation,
      viewport,
    ]
  );

  useEffect(() => {
    if (presentation !== "dock" || viewport === "mobile") {
      setDockTopPx(null);
      setDockDragging(false);
      dragStateRef.current = null;
      return undefined;
    }

    let restored = null;
    if (dockTopStorageKey) {
      try {
        const raw = localStorage.getItem(dockTopStorageKey);
        const parsed = Number(raw);
        if (Number.isFinite(parsed)) restored = parsed;
      } catch {}
    }

    setDockTopPx((current) =>
      clampDockTop(current ?? restored ?? getDefaultDockTop())
    );

    const handlePointerMove = (event) => {
      const drag = dragStateRef.current;
      if (!drag) return;
      if (
        drag.pointerId !== undefined &&
        event.pointerId !== undefined &&
        drag.pointerId !== event.pointerId
      ) {
        return;
      }

      drag.latestY = event.clientY;

      if (dragAnimationFrameRef.current) return;

      dragAnimationFrameRef.current = requestAnimationFrame(() => {
        dragAnimationFrameRef.current = null;
        const currentDrag = dragStateRef.current;
        if (!currentDrag) return;

        const nextTop = clampDockTop(
          currentDrag.startTop +
            (currentDrag.latestY - currentDrag.startY)
        );
        setDockTopPx(nextTop);
      });
    };

    const finishDrag = () => {
      if (!dragStateRef.current) return;

      dragStateRef.current = null;
      setDockDragging(false);

      if (dragAnimationFrameRef.current) {
        cancelAnimationFrame(dragAnimationFrameRef.current);
        dragAnimationFrameRef.current = null;
      }

      setDockTopPx((current) => {
        const next = clampDockTop(
          current ?? getDefaultDockTop()
        );
        persistDockTop(next);
        return next;
      });
    };

    const updateViewportMetrics = () => {
      try {
        const vv = window.visualViewport;
        const next = {
          top: Math.max(
            0,
            Number(vv?.offsetTop || 0)
          ),
          height: Math.max(
            1,
            Number(
              vv?.height ||
                window.innerHeight ||
                900
            )
          ),
        };

        setDockViewportMetrics((previous) => {
          if (
            previous.top === next.top &&
            previous.height === next.height
          ) {
            return previous;
          }
          return next;
        });
      } catch {}
    };

    const handleViewportChange = () => {
      updateViewportMetrics();
      setDockTopPx((current) =>
        clampDockTop(
          current ?? getDefaultDockTop()
        )
      );
    };

    const resizeObserver =
      typeof ResizeObserver !== "undefined" &&
      rootRef.current
        ? new ResizeObserver(() => {
            setDockTopPx((current) =>
              clampDockTop(
                current ?? getDefaultDockTop()
              )
            );
          })
        : null;

    resizeObserver?.observe?.(
      rootRef.current
    );

    updateViewportMetrics();

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", finishDrag);
    window.addEventListener("pointercancel", finishDrag);
    window.addEventListener("resize", handleViewportChange);
    window.visualViewport?.addEventListener?.(
      "resize",
      handleViewportChange
    );
    window.visualViewport?.addEventListener?.(
      "scroll",
      handleViewportChange
    );

    return () => {
      resizeObserver?.disconnect?.();
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", finishDrag);
      window.removeEventListener("pointercancel", finishDrag);
      window.removeEventListener("resize", handleViewportChange);
      window.visualViewport?.removeEventListener?.(
        "resize",
        handleViewportChange
      );
      window.visualViewport?.removeEventListener?.(
        "scroll",
        handleViewportChange
      );
    };
  }, [
    clampDockTop,
    dockTopStorageKey,
    getDefaultDockTop,
    persistDockTop,
    presentation,
    viewport,
  ]);

  useEffect(() => {
    setPageMapOpen(false);
    setVisibleSectionCount(PAGE_SECTION_INITIAL_VISIBLE);
    setPageRangeError("");
    setOverflowMenuOpen(false);
    setDeleteConfirmId("");

    if (!controlledDocumentPageScope) {
      setInternalDocumentPageScope(
        normalizeDocumentPageScope(defaultDocumentPageScope, activeDocument?.pageCount)
      );
    }
  }, [
    activeDocument?.id,
    activeDocument?.pageCount,
    allDocumentsSelected,
    controlledDocumentPageScope,
    defaultDocumentPageScope,
  ]);

  useEffect(() => {
    if (!actualDocumentPageScope) {
      setPageRangeStart("");
      setPageRangeEnd("");
      return;
    }
    setPageRangeStart(String(actualDocumentPageScope.startPage));
    setPageRangeEnd(String(actualDocumentPageScope.endPage));
  }, [
    actualDocumentPageScope?.startPage,
    actualDocumentPageScope?.endPage,
  ]);

  useEffect(() => {
    if (
      !actualOpen ||
      presentation !== "dock"
    ) {
      return undefined;
    }

    const onKey = (event) => {
      if (event.key === "Escape") {
        if (documentListOpen) {
          setDocumentListOpen(false);
          return;
        }
        if (deleteConfirmId) {
          setDeleteConfirmId("");
          return;
        }
        setOpenState(false);
      }
    };

    document.addEventListener(
      "keydown",
      onKey
    );
    return () =>
      document.removeEventListener(
        "keydown",
        onKey
      );
  }, [
    actualOpen,
    deleteConfirmId,
    documentListOpen,
    presentation,
    setOpenState,
  ]);

  useEffect(() => {
    if (!overflowMenuOpen) return undefined;

    const onPointerDown = (event) => {
      if (
        !rootRef.current?.contains(
          event.target
        )
      ) {
        setOverflowMenuOpen(false);
      }
    };

    document.addEventListener(
      "pointerdown",
      onPointerDown
    );
    return () =>
      document.removeEventListener(
        "pointerdown",
        onPointerDown
      );
  }, [overflowMenuOpen]);

  const allScope =
    !!allDocumentsSelected &&
    normalizedDocuments.length > 1;

  const rawActions = allScope
    ? ALL_DOCUMENT_ACTIONS
    : !activeDocument
    ? []
    : activeDocument.workerActionsProvided
    ? activeDocument.actions || []
    : activeDocument.actions?.length
    ? activeDocument.actions
    : TYPE_ACTIONS[activeDocument.type] ||
      DEFAULT_DOCUMENT_ACTIONS;

  // Deliberately NO maxVisibleActions slice. If the worker produced 10, 20, or
  // 40 genuine analysis actions, this component renders all of them.
  //
  // "Ask This Document" is different: it arms the normal chat composer so the
  // user can type a question. Keep one dedicated composer CTA below instead of
  // duplicating it inside Document Actions as if it were a one-click analysis.
  const normalizedActions = rawActions.map(normalizeAction);
  const actions = allScope
    ? normalizedActions
    : normalizedActions.filter(
        (action) =>
          !isAskDocumentComposerAction(action)
      );

  const emitAction = useCallback(
    (action) => {
      if (!action) return;

      if (
        !allScope &&
        activeDocument &&
        action.id === "page_map" &&
        pageMapMode === "inline"
      ) {
        setPageMapOpen(
          (previous) => !previous
        );
        return;
      }

      onAction?.({
        action,
        actionId: action.id,
        document: allScope
          ? null
          : activeDocument,
        documentId: allScope
          ? null
          : activeDocument?.id || null,
        documents: allScope
          ? normalizedDocuments
          : activeDocument
          ? [activeDocument]
          : [],
        scope: allScope
          ? "all_documents"
          : "document",
        source:
          presentation === "inline"
            ? "inline_document_control"
            : "document_intelligence",
      });
    },
    [
      activeDocument,
      allScope,
      normalizedDocuments,
      onAction,
      pageMapMode,
      presentation,
    ]
  );

  const handleAsk = useCallback(() => {
    onAskDocument?.({
      document: allScope
        ? null
        : activeDocument,
      documentId: allScope
        ? null
        : activeDocument?.id || null,
      documents: allScope
        ? normalizedDocuments
        : activeDocument
        ? [activeDocument]
        : [],
      scope: allScope
        ? "all_documents"
        : "document",
      source:
        presentation === "inline"
          ? "inline_document_control"
          : "document_intelligence",
    });
  }, [
    activeDocument,
    allScope,
    normalizedDocuments,
    onAskDocument,
    presentation,
  ]);

  const handlePageMapSelect = useCallback(
    (item, suppliedScope = null) => {
      if (!activeDocument || actualDocumentChatMode === "off") return;

      const scope =
        normalizeDocumentPageScope(
          suppliedScope || item,
          activeDocument?.pageCount
        ) || null;
      if (!scope) return;

      const selectedScope = chooseDocumentPageScope(scope, {
        source: "page_map",
        item,
      });
      if (!selectedScope) return;

      setPageRangeError("");
      onPageMapSelect?.({
        item,
        pageScope: selectedScope,
        pageNumbers: selectedScope.pageNumbers,
        startPage: selectedScope.startPage,
        endPage: selectedScope.endPage,
        document: activeDocument,
        documentId:
          activeDocument?.id || null,
        source:
          presentation === "inline"
            ? "inline_document_control"
            : "document_intelligence",
      });
    },
    [
      activeDocument,
      actualDocumentChatMode,
      chooseDocumentPageScope,
      onPageMapSelect,
      presentation,
    ]
  );

  const clearDocumentPageScope = useCallback(() => {
    setPageRangeError("");
    chooseDocumentPageScope(null, {
      source: "clear_page_scope",
    });
  }, [chooseDocumentPageScope]);

  const applyManualPageRange = useCallback(() => {
    if (!activeDocument || actualDocumentChatMode === "off") return;

    const start = toPositivePageNumber(pageRangeStart);
    const end = toPositivePageNumber(pageRangeEnd || pageRangeStart);
    const maxPage = toPositivePageNumber(activeDocument.pageCount);

    if (!start || !end) {
      setPageRangeError("Enter a valid starting and ending page.");
      return;
    }

    const low = Math.min(start, end);
    const high = Math.max(start, end);

    if (maxPage && high > maxPage) {
      setPageRangeError(`Choose pages within 1–${maxPage}.`);
      return;
    }

    const item = {
      startPage: low,
      endPage: high,
      label: low === high ? `Page ${low}` : `Pages ${low}–${high}`,
      source: "manual_page_range",
    };
    const scope = chooseDocumentPageScope(item, {
      source: "manual_page_range",
      item,
    });
    if (!scope) {
      setPageRangeError("Choose a valid page range.");
      return;
    }

    setPageRangeStart(String(scope.startPage));
    setPageRangeEnd(String(scope.endPage));
    setPageRangeError("");

    onPageMapSelect?.({
      item,
      pageScope: scope,
      pageNumbers: scope.pageNumbers,
      startPage: scope.startPage,
      endPage: scope.endPage,
      document: activeDocument,
      documentId: activeDocument.id,
      source:
        presentation === "inline"
          ? "inline_document_control"
          : "document_intelligence",
    });
  }, [
    activeDocument,
    actualDocumentChatMode,
    chooseDocumentPageScope,
    onPageMapSelect,
    pageRangeEnd,
    pageRangeStart,
    presentation,
  ]);

  const requestDelete = useCallback(() => {
    if (!activeDocument?.id) return;
    setOverflowMenuOpen(false);
    setDeleteConfirmId(
      activeDocument.id
    );
  }, [activeDocument?.id]);

  const confirmDelete = useCallback(
    async () => {
      const target = activeDocument;
      if (
        !target?.id ||
        deleteBusyId
      ) {
        return;
      }

      setDeleteBusyId(target.id);

      try {
        if (catalogEnabled) {
          if (!apiBase) {
            throw new Error(
              "Document Intelligence is temporarily unavailable."
            );
          }

          const token = await getIdToken();
          const response = await fetch(
            `${apiBase}/document-intelligence/documents/${encodeURIComponent(
              target.id
            )}`,
            {
              method: "DELETE",
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          await readDocumentIntelligenceResponse(
            response,
            "Unable to delete this document."
          );

          removeCatalogDocument(target.id);
        } else if (
          typeof onDeleteDocument ===
          "function"
        ) {
          await onDeleteDocument(target);
        } else if (
          typeof onRemoveDocument ===
          "function"
        ) {
          await onRemoveDocument(target);
        }

        if (
          catalogEnabled &&
          typeof onDeleteDocument ===
            "function"
        ) {
          onDeleteDocument(target);
        }
        if (
          catalogEnabled &&
          typeof onRemoveDocument ===
            "function"
        ) {
          onRemoveDocument(target);
        }

        setDeleteConfirmId("");
      } catch (error) {
        console.warn(
          "[DocumentIntelligence] delete failed:",
          error
        );
        setCatalogError(
          "We couldn't remove this document right now. Please try again."
        );
      } finally {
        setDeleteBusyId("");
      }
    },
    [
      activeDocument,
      apiBase,
      catalogEnabled,
      deleteBusyId,
      getIdToken,
      onDeleteDocument,
      onRemoveDocument,
      removeCatalogDocument,
    ]
  );

  // Self-managed dock remains mounted while loading but renders nothing until
  // at least one owner-visible document exists.
  if (
    !normalizedDocuments.length
  ) {
    return null;
  }

  const documentCount =
    normalizedDocuments.length;
  const documentCountLabel =
    catalogEnabled &&
    catalogPagination.hasMore
      ? `${documentCount}+`
      : String(documentCount);

  const activeStatus = activeDocument
    ? statusMeta(activeDocument.status)
    : null;

  const pageText =
    activeDocument?.pageCount === null ||
    activeDocument?.pageCount === undefined
      ? ""
      : `${activeDocument.pageCount} page${
          activeDocument.pageCount === 1
            ? ""
            : "s"
        }`;

  const activeSectionItems = Array.isArray(activeDocument?.pageMap)
    ? activeDocument.pageMap
    : [];
  const activeSectionCount = activeSectionItems.length;
  const documentPageControlsDisabled =
    actualDocumentChatMode === "off" ||
    activeStatus?.tone === "working" ||
    activeStatus?.tone === "error";
  const activePageScopeRange =
    pageScopeRangeText(actualDocumentPageScope);
  const activePageScopeLabel = actualDocumentPageScope?.label || "";
  const documentSectionsSummary = actualDocumentPageScope
    ? `${activePageScopeRange}${activePageScopeLabel && activePageScopeLabel !== activePageScopeRange ? ` · ${activePageScopeLabel}` : ""}`
    : activeSectionCount
    ? `${activeSectionCount} section${activeSectionCount === 1 ? "" : "s"} available`
    : activeDocument?.pageCount
    ? `Choose a page range from ${activeDocument.pageCount} pages`
    : "Choose a document section or page range";

  const rootStyle = {
    "--di-brand": brandColor || BRAND,
    ...(style || {}),
  };

  const activeProgressRaw =
    activeDocument?.progressPercent;

  const activeHasProgressPercent =
    activeProgressRaw !== null &&
    activeProgressRaw !== undefined &&
    String(activeProgressRaw).trim() !== "";

  const activeParsedProgressPercent =
    activeHasProgressPercent
      ? Number(activeProgressRaw)
      : Number.NaN;

  const activeProgressPercent =
    Number.isFinite(activeParsedProgressPercent)
      ? Math.max(
          0,
          Math.min(
            100,
            activeParsedProgressPercent
          )
        )
      : null;

  const activeProgressStage =
    activeDocument?.progressStage ||
    activeDocument?.status ||
    "";

  const activeProgressMessage =
    activeDocument?.progressMessage ||
    (activeStatus?.tone === "ready"
      ? "Document Intelligence is ready."
      : activeStatus?.tone === "working"
      ? humanizeStage(
          activeProgressStage
        ) || "Processing document…"
      : "Document Intelligence needs attention.");

  const canDeleteActive =
    !allScope &&
    !!activeDocument &&
    (
      catalogEnabled ||
      typeof onDeleteDocument ===
        "function" ||
      typeof onRemoveDocument ===
        "function"
    );

  const content = (
    <section
      ref={rootRef}
      className={`document-intelligence di-${presentation} di-${viewport} ${
        className || ""
      }`.trim()}
      style={rootStyle}
      aria-label={title}
    >
      {presentation === "dock" &&
      viewport === "mobile" ? (
        <div
          className="di-sheet-grabber"
          aria-hidden="true"
        />
      ) : null}

      <header className="di-header">
        <div className="di-header-main">
          <span
            className="di-header-icon"
            aria-hidden="true"
          >
            <FiLayers />
          </span>
          <div className="di-header-copy">
            <div className="di-eyebrow">
              ASKLAWLY
            </div>
            <div className="di-title">
              {title}
            </div>
          </div>
        </div>

        {presentation === "dock" ? (
          <div className="di-header-actions">
            {viewport !== "mobile" ? (
              <button
                type="button"
                className={`di-drag-handle ${
                  dockDragging ? "is-dragging" : ""
                }`}
                onPointerDown={beginVerticalDrag}
                aria-label="Move Document Intelligence panel vertically"
                title="Drag up or down"
              >
                <FiMoreHorizontal aria-hidden="true" />
              </button>
            ) : null}

            <button
              type="button"
              className="di-icon-button"
              onClick={() =>
                setOpenState(false)
              }
              aria-label={
                viewport === "mobile"
                  ? "Close document intelligence"
                  : "Collapse document intelligence"
              }
            >
              {viewport === "mobile" ? (
                <FiX />
              ) : (
                <FiMinimize2 />
              )}
            </button>
          </div>
        ) : null}
      </header>

      {documentCount > 1 ? (
        <div className="di-scope-block">
          <button
            type="button"
            className={`di-document-selector ${
              documentListOpen
                ? "is-open"
                : ""
            }`}
            onClick={() =>
              setDocumentListOpen(
                (previous) => !previous
              )
            }
            aria-expanded={
              documentListOpen
            }
            aria-haspopup="listbox"
          >
            <span className="di-selector-icon">
              <FiFileText />
            </span>
            <span className="di-selector-copy">
              <strong>
                {allScope
                  ? `All documents (${documentCountLabel})`
                  : activeDocument?.name}
              </strong>
              <small>
                {allScope
                  ? "Cross-document scope"
                  : [
                      pageText,
                      activeStatus?.label,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
              </small>
            </span>
            <FiChevronDown className="di-selector-chevron" />
          </button>

          {documentListOpen ? (
            <div
              className="di-document-list"
              role="listbox"
              aria-label="Choose document"
            >
              {allowAllDocuments ? (
                <button
                  type="button"
                  className={`di-document-option ${
                    allScope
                      ? "is-active"
                      : ""
                  }`}
                  onClick={() => {
                    onAllDocumentsChange?.(
                      true
                    );
                    setDocumentListOpen(
                      false
                    );
                  }}
                  role="option"
                  aria-selected={allScope}
                >
                  <span className="di-document-option-icon">
                    <FiLayers />
                  </span>
                  <span className="di-document-option-copy">
                    <strong>
                      All documents
                    </strong>
                    <small>
                      Compare or analyze
                      across{" "}
                      {documentCountLabel}{" "}
                      documents
                    </small>
                  </span>
                  {allScope ? (
                    <FiCheck className="di-option-check" />
                  ) : null}
                </button>
              ) : null}

              {normalizedDocuments.map(
                (doc) => {
                  const selected =
                    !allScope &&
                    doc.id ===
                      activeDocument?.id;
                  const status =
                    statusMeta(doc.status);

                  return (
                    <button
                      type="button"
                      key={doc.id}
                      className={`di-document-option ${
                        selected
                          ? "is-active"
                          : ""
                      }`}
                      onClick={() => {
                        chooseDocument(doc);
                        setDocumentListOpen(
                          false
                        );
                      }}
                      role="option"
                      aria-selected={selected}
                      aria-label={`${
                        doc.name
                      }. ${
                        status.label
                      }`}
                    >
                      <span className="di-document-option-icon">
                        <FiFileText />
                      </span>
                      <span className="di-document-option-copy">
                        <strong>
                          {doc.name}
                        </strong>
                        <small>
                          {[
                            doc.pageCount !==
                            null
                              ? `${
                                  doc.pageCount
                                } page${
                                  doc.pageCount ===
                                  1
                                    ? ""
                                    : "s"
                                }`
                              : "",
                            status.label,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </small>
                      </span>
                      {selected ? (
                        <FiCheck className="di-option-check" />
                      ) : null}
                    </button>
                  );
                }
              )}

              {catalogEnabled &&
              catalogPagination.hasMore ? (
                <div className="di-document-list-footer">
                  <button
                    type="button"
                    className="di-load-more"
                    onClick={
                      handleLoadMore
                    }
                    disabled={
                      catalogLoadingMore
                    }
                  >
                    {catalogLoadingMore ? (
                      <>
                        <FiRefreshCw className="di-spin" />
                        Loading…
                      </>
                    ) : (
                      <>
                        <FiChevronDown />
                        Load{" "}
                        {
                          resolvedCatalogLimit
                        }{" "}
                        more
                      </>
                    )}
                  </button>
                </div>
              ) : null}

              {catalogError &&
              catalogEnabled ? (
                <div className="di-catalog-error">
                  {catalogError}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {!allScope &&
      activeDocument ? (
        <>
          <div className="di-active-document">
            <div
              className="di-file-mark"
              aria-hidden="true"
            >
              <FiFileText />
            </div>

            <div className="di-active-document-copy">
              <strong>
                {activeDocument.name}
              </strong>
              <span>
                {pageText ? (
                  <span>{pageText}</span>
                ) : null}
                {showInlineStatus ? (
                  <DocumentStatus
                    status={
                      activeDocument.status
                    }
                  />
                ) : null}
              </span>
            </div>

            {onOpenDocument ||
            onRefreshAction ||
            canDeleteActive ? (
              <div className="di-document-utilities">
                {onOpenDocument ? (
                  <button
                    type="button"
                    className="di-view-file-button"
                    onClick={() =>
                      onOpenDocument(
                        activeDocument,
                        {
                          source:
                            presentation ===
                            "inline"
                              ? "inline_document_control"
                              : "document_intelligence",
                          originalFile: true,
                          fileAccessGateway: true,
                        }
                      )
                    }
                    aria-label={`View original file ${activeDocument.name}`}
                    title="View File"
                  >
                    <FiEye aria-hidden="true" />
                    <span>View File</span>
                  </button>
                ) : null}

                {onRefreshAction ||
                canDeleteActive ? (
                  <div className="di-overflow-wrap">
                    <button
                      type="button"
                      className="di-icon-button di-more-button"
                      aria-label="Document options"
                      aria-expanded={
                        overflowMenuOpen
                      }
                      onClick={() =>
                        setOverflowMenuOpen(
                          (previous) =>
                            !previous
                        )
                      }
                    >
                      <FiMoreHorizontal />
                    </button>

                    {overflowMenuOpen ? (
                      <div className="di-overflow-menu">
                        {onRefreshAction ? (
                          <button
                            type="button"
                            onClick={() => {
                              setOverflowMenuOpen(
                                false
                              );
                              onRefreshAction(
                                activeDocument
                              );
                            }}
                          >
                            <FiRefreshCw />{" "}
                            Refresh
                            intelligence
                          </button>
                        ) : null}

                        {canDeleteActive ? (
                          <button
                            type="button"
                            className="is-danger"
                            onClick={
                              requestDelete
                            }
                          >
                            <FiTrash2 /> Delete
                            document
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          {deleteConfirmId ===
          activeDocument.id ? (
            <div className="di-delete-confirm">
              <strong>
                Remove this document?
              </strong>
              <p>
                This document will be removed
                from your Document Intelligence
                list. Are you sure you want to
                continue?
              </p>
              <div className="di-delete-confirm-actions">
                <button
                  type="button"
                  onClick={() =>
                    setDeleteConfirmId(
                      ""
                    )
                  }
                  disabled={
                    deleteBusyId ===
                    activeDocument.id
                  }
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="is-danger"
                  onClick={
                    confirmDelete
                  }
                  disabled={
                    deleteBusyId ===
                    activeDocument.id
                  }
                >
                  {deleteBusyId ===
                  activeDocument.id
                    ? "Deleting…"
                    : "Remove"}
                </button>
              </div>
            </div>
          ) : null}
        </>
      ) : allScope ? (
        <div className="di-all-documents-summary">
          <span className="di-file-mark">
            <FiLayers />
          </span>
          <div>
            <strong>
              All documents
            </strong>
            <span>
              {documentCountLabel} active
              documents
            </span>
          </div>
        </div>
      ) : (
        <div className="di-all-documents-summary">
          <span className="di-file-mark">
            <FiFileText />
          </span>
          <div>
            <strong>
              Document Intelligence
            </strong>
            <span>
              Restoring your document selection…
            </span>
          </div>
        </div>
      )}

      {!allScope && activeDocument ? (
        <>
          <div className={`di-chat-use ${actualDocumentChatMode === "off" ? "is-off" : ""}`}>
            <div className="di-chat-use-head">
              <div className="di-chat-use-copy">
                <span className="di-chat-use-copy-icon" aria-hidden="true">
                  <FiMessageSquare />
                </span>
                <span className="di-chat-use-copy-text">
                  <strong>Use in Chat</strong>
                  <span>Choose how Lexa uses this document</span>
                </span>
              </div>

              <div
                className="di-chat-use-segment"
                role="group"
                aria-label="Use this document in chat"
              >
                <button
                  type="button"
                  className={`di-chat-use-option ${
                    actualDocumentChatMode === "auto" ? "is-active" : ""
                  }`}
                  onClick={() => chooseDocumentChatMode("auto")}
                  aria-pressed={actualDocumentChatMode === "auto"}
                >
                  Auto
                </button>
                <button
                  type="button"
                  className={`di-chat-use-option ${
                    actualDocumentChatMode === "force" ? "is-active" : ""
                  }`}
                  onClick={() => chooseDocumentChatMode("force")}
                  aria-pressed={actualDocumentChatMode === "force"}
                >
                  Always Use
                </button>
                <button
                  type="button"
                  className={`di-chat-use-option is-off ${
                    actualDocumentChatMode === "off" ? "is-active" : ""
                  }`}
                  onClick={() => chooseDocumentChatMode("off")}
                  aria-pressed={actualDocumentChatMode === "off"}
                >
                  Off
                </button>
              </div>
            </div>

            <p className="di-chat-use-help">
              {actualDocumentChatMode === "force"
                ? "Lexa uses this document for every question in this chat until you change this setting."
                : actualDocumentChatMode === "off"
                ? "Lexa will not use this document in Chat until you change this setting."
                : "Lexa uses this document only when your question appears related to it."}
            </p>
          </div>

          <div className={`di-section-focus ${documentPageControlsDisabled ? "is-disabled" : ""}`}>
            <button
              type="button"
              className={`di-section-focus-toggle ${pageMapOpen ? "is-open" : ""}`}
              onClick={() => setPageMapOpen((previous) => !previous)}
              disabled={activeStatus?.tone === "working" || activeStatus?.tone === "error"}
              aria-expanded={pageMapOpen}
              aria-controls={`di-document-sections-${activeDocument.id}`}
            >
              <span className="di-section-focus-icon" aria-hidden="true">
                <FiMap />
              </span>
              <span className="di-section-focus-copy">
                <strong>Document Sections</strong>
                <span>{documentSectionsSummary}</span>
              </span>
              <span className="di-section-focus-count">
                {activeSectionCount
                  ? `${activeSectionCount} section${activeSectionCount === 1 ? "" : "s"}`
                  : activeDocument.pageCount
                  ? `${activeDocument.pageCount} pages`
                  : ""}
              </span>
              <FiChevronDown className="di-section-focus-chevron" aria-hidden="true" />
            </button>

            {pageMapOpen ? (
              <div
                id={`di-document-sections-${activeDocument.id}`}
                className="di-section-focus-body"
              >
                {actualDocumentChatMode === "off" ? (
                  <div className="di-section-focus-disabled-note">
                    This document is Off for Chat. Choose Auto or Always Use before selecting a section or page range.
                  </div>
                ) : (
                  <>
                    <p className="di-section-focus-note">
                      Focus the next document question on a mapped section or a specific page range.
                    </p>

                    {actualDocumentPageScope ? (
                      <div className="di-page-scope-chip">
                        <span className="di-page-scope-chip-copy">
                          <strong>Focused pages</strong>
                          <span>{documentSectionsSummary}</span>
                        </span>
                        <button
                          type="button"
                          className="di-page-scope-clear"
                          onClick={clearDocumentPageScope}
                          aria-label="Clear focused pages"
                          disabled={documentPageControlsDisabled}
                        >
                          <FiX />
                        </button>
                      </div>
                    ) : null}

                    <PageMap
                      items={activeSectionItems}
                      pageCount={activeDocument.pageCount}
                      visibleCount={visibleSectionCount}
                      selectedScope={actualDocumentPageScope}
                      disabled={documentPageControlsDisabled}
                      onSelect={handlePageMapSelect}
                      onShowMore={() =>
                        setVisibleSectionCount((count) =>
                          Math.min(
                            activeSectionCount,
                            count + PAGE_SECTION_REVEAL_STEP
                          )
                        )
                      }
                      onShowLess={() =>
                        setVisibleSectionCount(PAGE_SECTION_INITIAL_VISIBLE)
                      }
                    />

                    <div className="di-page-range-form">
                      <div className="di-page-range-form-title">
                        <strong>Choose page range</strong>
                        <span>
                          {activeDocument.pageCount
                            ? `1–${activeDocument.pageCount}`
                            : "Exact pages"}
                        </span>
                      </div>
                      <div className="di-page-range-fields">
                        <span className="di-page-range-field">
                          <label htmlFor={`di-page-start-${activeDocument.id}`}>
                            From
                          </label>
                          <input
                            id={`di-page-start-${activeDocument.id}`}
                            type="number"
                            min="1"
                            max={activeDocument.pageCount || undefined}
                            inputMode="numeric"
                            value={pageRangeStart}
                            onChange={(event) => {
                              setPageRangeStart(event.target.value);
                              setPageRangeError("");
                            }}
                            disabled={documentPageControlsDisabled}
                            placeholder="1"
                          />
                        </span>
                        <span className="di-page-range-field">
                          <label htmlFor={`di-page-end-${activeDocument.id}`}>
                            To
                          </label>
                          <input
                            id={`di-page-end-${activeDocument.id}`}
                            type="number"
                            min="1"
                            max={activeDocument.pageCount || undefined}
                            inputMode="numeric"
                            value={pageRangeEnd}
                            onChange={(event) => {
                              setPageRangeEnd(event.target.value);
                              setPageRangeError("");
                            }}
                            disabled={documentPageControlsDisabled}
                            placeholder={String(activeDocument.pageCount || 1)}
                          />
                        </span>
                        <button
                          type="button"
                          className="di-page-range-apply"
                          onClick={applyManualPageRange}
                          disabled={documentPageControlsDisabled}
                        >
                          Focus
                        </button>
                      </div>
                      {pageRangeError ? (
                        <div className="di-page-range-error" role="alert">
                          {pageRangeError}
                        </div>
                      ) : null}
                    </div>
                  </>
                )}
              </div>
            ) : null}
          </div>
        </>
      ) : null}

      <div className="di-divider" />

      <div className="di-section-heading">
        <span>
          {allScope
            ? "Cross-document actions"
            : "Document actions"}
        </span>

        {!allScope &&
        activeStatus?.tone ===
          "working" ? (
          <span className="di-soft-note">
            Available when ready
          </span>
        ) : actions.length ? (
          <span className="di-soft-note">
            {actions.length} available
          </span>
        ) : null}
      </div>

      {actions.length ? (
        <div className="di-action-grid">
          {actions.map(
            (action, index) => {
              const stateKey = `${
                allScope
                  ? "all"
                  : activeDocument?.id ||
                    "document"
              }:${action.id}`;
              const state =
                actionState?.[
                  stateKey
                ] ||
                actionState?.[
                  action.id
                ] ||
                {};
              const busy =
                !!state?.loading ||
                !!state?.busy;
              const completed =
                !!state?.completed ||
                (!allScope &&
                  activeDocument?.completedActions?.has?.(
                    action.id
                  ));
              const disabled =
                !!action.disabled ||
                busy ||
                (!allScope &&
                  (activeDocument?.status === "error" ||
                    actualDocumentChatMode === "off"));

              // No native title tooltip here. Native browser tooltips cannot be
              // constrained to the application viewport and were the source of
              // the long off-screen hover box shown in the screenshot.
              const accessibleLabel =
                action.description
                  ? `${action.label}. ${action.description}`
                  : action.label;

              return (
                <button
                  type="button"
                  key={`${action.id}-${index}`}
                  className={`di-action ${
                    completed
                      ? "is-complete"
                      : ""
                  } ${
                    busy
                      ? "is-busy"
                      : ""
                  }`}
                  onClick={() =>
                    emitAction(action)
                  }
                  disabled={disabled}
                  aria-label={
                    accessibleLabel
                  }
                >
                  <span
                    className="di-action-icon"
                    aria-hidden="true"
                  >
                    {busy ? (
                      <FiRefreshCw className="di-spin" />
                    ) : (
                      actionIcon(
                        action.icon ||
                          action.id
                      )
                    )}
                  </span>
                  <span className="di-action-label">
                    {action.label}
                  </span>
                  {completed ? (
                    <FiCheckCircle
                      className="di-action-complete"
                      aria-label="Completed"
                    />
                  ) : null}
                </button>
              );
            }
          )}
        </div>
      ) : (
        <div className="di-empty-note">
          {activeStatus?.tone ===
          "working"
            ? "Document actions will appear when intelligence is ready."
            : "No document actions are available for this document."}
        </div>
      )}

      <button
        type="button"
        className="di-ask-button"
        onClick={handleAsk}
        disabled={
          !onAskDocument ||
          (!allScope &&
            (!activeDocument || actualDocumentChatMode === "off"))
        }
      >
        <span className="di-ask-icon">
          <FiMessageSquare />
        </span>
        <span className="di-ask-copy">
          <strong>
            {allScope
              ? "Ask all documents"
              : "Ask this document"}
          </strong>
          <small>
            {allScope
              ? "Scope your next question across the active set"
              : actualDocumentChatMode === "off"
              ? "Turn on Auto or Always Use to ask this document"
              : actualDocumentPageScope
              ? `Next question focused on ${activePageScopeRange}`
              : "Add this document to your message box"}
          </small>
        </span>
        <FiChevronsRight className="di-ask-arrow" />
      </button>

      {presentation === "dock" &&
      showProgress &&
      !allScope &&
      activeDocument &&
      activeStatus?.tone === "working" ? (
        <div
          className="di-progress-footer"
          aria-live="polite"
        >
          <div className="di-progress-heading">
            <span>
              <FiRefreshCw className="di-spin" />
              Processing status
            </span>
            {activeProgressPercent !== null ? (
              <span className="di-progress-value">
                {Math.round(activeProgressPercent)}%
              </span>
            ) : null}
          </div>
          <div className="di-progress-track">
            <div
              className={`di-progress-bar ${
                activeProgressPercent === null
                  ? "is-indeterminate"
                  : ""
              }`}
              style={{
                width:
                  activeProgressPercent !== null
                    ? `${activeProgressPercent}%`
                    : "22%",
              }}
            />
          </div>
          <div className="di-progress-copy">
            {activeProgressMessage}
          </div>
        </div>
      ) : null}

      {presentation === "inline" ? (
        <div className="di-inline-footer">
          <span>
            <FiCheckCircle /> Document
            controls stay available in the
            floating document intelligence
            panel.
          </span>
        </div>
      ) : null}
    </section>
  );

  if (presentation === "inline") {
    return content;
  }

  if (!actualOpen) {
    return (
      <button
        type="button"
        className={`di-edge-rail di-edge-${viewport}`}
        style={{
          "--di-brand":
            brandColor || BRAND,
        }}
        onMouseEnter={scheduleHoverOpen}
        onMouseLeave={cancelHoverOpen}
        onFocus={scheduleHoverOpen}
        onBlur={cancelHoverOpen}
        onClick={() => {
          cancelHoverOpen();
          setOpenState(true);
        }}
        aria-label={`Open ${title}. ${documentCountLabel} documents.`}
      >
        <span
          className="di-edge-line"
          aria-hidden="true"
        />
        <span className="di-edge-icon">
          <FiFileText />
        </span>
        <span className="di-edge-count">
          {documentCountLabel}
        </span>
        <FiChevronsLeft
          className="di-edge-chevron"
          aria-hidden="true"
        />
      </button>
    );
  }

  return (
    <>
      {viewport !== "desktop" ? (
        <button
          type="button"
          className={`di-backdrop di-backdrop-${viewport}`}
          onClick={() =>
            setOpenState(false)
          }
          aria-label="Close document intelligence"
        />
      ) : null}

      <div
        className={`di-floating-shell di-shell-${viewport}`}
        style={
          viewport !== "mobile" &&
          dockTopPx !== null &&
          dockAvailableHeightPx !== null
            ? {
                top: `${dockTopPx}px`,
                bottom: "auto",
                height: `${dockAvailableHeightPx}px`,
                maxHeight: `${dockAvailableHeightPx}px`,
              }
            : undefined
        }
      >
        {content}
      </div>
    </>
  );
}
